import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { getDanceStyleLabel } from "../../config/danceStyles";
import {
    mdiAccountGroup,
    mdiCheck,
    mdiClose,
    mdiAccountDetails,
    mdiEmailOutline,
    mdiQrcode,
    mdiCreditCardCheck,
    mdiCreditCardSyncOutline,
    mdiCreditCardClockOutline,
    mdiCreditCardPlusOutline,
    mdiTrashCanOutline,
    mdiPencilOutline,
    mdiAccountOff,
    mdiAccountCheck,
    mdiAccountMultiplePlusOutline,
    mdiHelpCircleOutline,
    mdiHelpCircle,
    mdiPaw,
    mdiAccountCircleOutline,
    mdiAccountBoxEditOutline,
    mdiAccountEditOutline,
    mdiDownload,
    mdiChevronDown,
    mdiChevronUp,
    mdiCheckCircle,
    mdiCloseCircle,
    mdiMinusCircleOutline,
    mdiCashRefund,
    mdiCalendarRemove,
    mdiSwapHorizontal,
    mdiPrinter,
    mdiAccountPlusOutline,
    mdiCalendarArrowRight,
} from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import AppHeader from "../../components/AppHeader";
import BaseModal from "../../components/BaseModal";
import FolderTabs from "../../components/FolderTabs";
import PaymentsAuditTab from "./PaymentsAuditTab";
import { classApi } from "../../api/classes";
import { organizationApi } from "../../api/organization";
import ConfirmationModal from "../../components/ConfirmationModal";
import EmailReviewModal from "../../components/communications/EmailReviewModal";
import AdminPaymentModal from "../../components/classes/AdminPaymentModal";
import RegisterStudentModal from "../../components/classes/RegisterStudentModal";
import ClassStudentsHelpModal from "../../components/ClassStudentsHelpModal";
import CustomFormInput from "../../components/forms/CustomFormInput";
import StudentAvatarUpload from "../../components/shared/StudentAvatarUpload";
import StudentPhotoPreviewModal from "../../components/shared/StudentPhotoPreviewModal";
import { cacheImage, getCachedImage, getOrFetchImage, base64ToBlobUrl } from "../../utils/imageCache";
import { formatCurrencyFromMinor } from "../../utils/currency";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassStudentsPage.css";
import "./ClassStudentsPage.print.css";

// Returns icon path + tooltip label for a student's account type
function getAccountTypeIcon(source) {
    if (source === "student-profile") {
        return { icon: mdiAccountCircleOutline, label: "School account only (no Dance Localé account)" };
    }
    return { icon: mdiPaw, label: "Dance Localé platform member" };
}

// ---------------------------------------------------------------------------
// ProspectDetailModal
// ---------------------------------------------------------------------------
function ProspectDetailModal({ prospect, onClose, onReload }) {
    const [detail, setDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingLeadSource, setEditingLeadSource] = useState(false);
    const [leadSourceValue, setLeadSourceValue] = useState(prospect?.leadSource || '');

    useEffect(() => {
        if (!prospect) return;
        setIsLoading(true);
        classApi.getProspectDetail(prospect.id)
            .then(r => { if (r.ok) setDetail(r.data); })
            .finally(() => setIsLoading(false));
    }, [prospect?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMarkConverted = async () => {
        setIsSaving(true);
        try {
            const r = await classApi.markProspectConverted(prospect.id);
            if (r.ok) { toast.success('Marked as converted'); onReload?.(); onClose(); }
            else toast.error(r.data?.message || 'Failed to update');
        } catch { toast.error('Failed to update'); }
        finally { setIsSaving(false); }
    };

    const handleMarkLost = async () => {
        setIsSaving(true);
        try {
            const r = await classApi.markProspectLost(prospect.id);
            if (r.ok) { toast.success('Marked as lost'); onReload?.(); onClose(); }
            else toast.error(r.data?.message || 'Failed to update');
        } catch { toast.error('Failed to update'); }
        finally { setIsSaving(false); }
    };

    const handleSaveLeadSource = async () => {
        setIsSaving(true);
        try {
            const r = await classApi.updateProspectLeadSource(prospect.id, leadSourceValue || null, '');
            if (r.ok) { toast.success('Lead source updated'); setEditingLeadSource(false); onReload?.(); }
            else toast.error(r.data?.message || 'Failed to update');
        } catch { toast.error('Failed to update'); }
        finally { setIsSaving(false); }
    };

    const journey = detail?.journey;
    const isActive = prospect?.prospectStatus === 'active';

    return (
        <BaseModal
            isOpen={!!prospect}
            onClose={onClose}
            title={`${prospect?.firstName || ''} ${prospect?.lastName || ''}`.trim()}
            icon={mdiAccountCircleOutline}
            maxWidth={520}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>Close</button>
                    {isActive && (
                        <>
                            <button className="base-modal-secondary-btn" onClick={handleMarkLost} disabled={isSaving}>Mark Lost</button>
                            <button className="base-modal-save-btn" onClick={handleMarkConverted} disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Mark Converted'}
                            </button>
                        </>
                    )}
                </>
            }
        >
            {isLoading ? (
                <p className="class-students-empty">Loading...</p>
            ) : (
                <div className="prospect-detail-body">
                    <div className="prospect-detail-row">
                        <span className="prospect-detail-label">Email</span>
                        <span className="prospect-detail-value">{prospect?.email || 'Not provided'}</span>
                    </div>
                    <div className="prospect-detail-row">
                        <span className="prospect-detail-label">Status</span>
                        <span className={`prospect-source-pill prospect-source-pill--${prospect?.prospectStatus || 'active'}`}>
                            {prospect?.prospectStatus || 'active'}
                        </span>
                    </div>
                    <div className="prospect-detail-row">
                        <span className="prospect-detail-label">Sources</span>
                        <span className="prospect-detail-value">
                            {(prospect?.prospectSources || []).join(', ') || 'Unknown'}
                        </span>
                    </div>
                    <div className="prospect-detail-row">
                        <span className="prospect-detail-label">Lead source</span>
                        {editingLeadSource ? (
                            <div className="prospect-lead-source-edit">
                                <select
                                    className="prospect-lead-source-select"
                                    value={leadSourceValue}
                                    onChange={e => setLeadSourceValue(e.target.value)}
                                >
                                    <option value="">Unknown</option>
                                    <option value="facebook_ad">Facebook ad</option>
                                    <option value="instagram">Instagram</option>
                                    <option value="google_search">Google search</option>
                                    <option value="friend_referral">Friend referral</option>
                                    <option value="existing_student_referral">Existing student referral</option>
                                    <option value="walk_past_studio">Walk past studio</option>
                                    <option value="flyer">Flyer</option>
                                    <option value="email">Email</option>
                                    <option value="other">Other</option>
                                </select>
                                <button className="prospect-lead-source-save-btn" onClick={handleSaveLeadSource} disabled={isSaving}>Save</button>
                                <button className="prospect-lead-source-cancel-btn" onClick={() => { setEditingLeadSource(false); setLeadSourceValue(prospect?.leadSource || ''); }}>Cancel</button>
                            </div>
                        ) : (
                            <span className="prospect-detail-value">
                                {prospect?.leadSourceLabel || 'Unknown'}
                                <button className="prospect-edit-inline-btn" onClick={() => setEditingLeadSource(true)}>
                                    <Icon path={mdiPencilOutline} size={0.7} />
                                </button>
                            </span>
                        )}
                    </div>
                    {prospect?.trialDealName && (
                        <div className="prospect-detail-row">
                            <span className="prospect-detail-label">Trial deal</span>
                            <span className="prospect-detail-value">{prospect.trialDealName}</span>
                        </div>
                    )}

                    {journey?.trialDeals?.length > 0 && (
                        <div className="prospect-detail-section">
                            <p className="prospect-detail-section-heading">Trial deals purchased</p>
                            {journey.trialDeals.map((td, i) => (
                                <div key={i} className="prospect-detail-journey-row">
                                    <span>{td.dealName}</span>
                                    <span className={`prospect-payment-pill prospect-payment-pill--${td.paymentStatus || 'unknown'}`}>
                                        {td.paymentStatus === 'paid'
                                            ? 'Paid'
                                            : td.paymentStatus === 'refunded'
                                              ? 'Refunded'
                                              : 'Awaiting payment'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {journey?.enrolments?.length > 0 && (
                        <div className="prospect-detail-section">
                            <p className="prospect-detail-section-heading">Enrolments</p>
                            {journey.enrolments.map((e, i) => (
                                <div key={i} className="prospect-detail-journey-row">
                                    <span>{e.className || e.classId}{e.termName ? ` · ${e.termName}` : ''}</span>
                                    <span className={`prospect-source-pill prospect-source-pill--${e.status}`}>{e.status}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </BaseModal>
    );
}

// ---------------------------------------------------------------------------
// ProspectsTab — Unified Prospects pipeline + Enquiries + Upgrade Requests
// ---------------------------------------------------------------------------
const PROSPECT_SOURCE_LABELS = {
    trial: 'Trial',
    walkin: 'Walk-in',
    enquiry: 'Enquiry',
    import: 'Import',
    manual: 'Manual',
    request: 'Request',
};

function ProspectsTab({ onEnrolStudent }) {
    // Requests section (enquiries + upgrade requests)
    const [enquiries, setEnquiries] = useState([]);
    const [dismissedEnquiries, setDismissedEnquiries] = useState([]);
    const [showDismissed, setShowDismissed] = useState(false);
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [requestsLoading, setRequestsLoading] = useState(true);

    // Prospects pipeline
    const [prospects, setProspects] = useState([]);
    const [prospectsLoading, setProspectsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('active');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const loadRequests = useCallback(async () => {
        setRequestsLoading(true);
        try {
            const [eq, uq, dq] = await Promise.all([
                classApi.getEnquiries(),
                classApi.getUpgradeRequests(),
                classApi.getEnquiries('dismissed'),
            ]);
            const eqList = eq.ok ? eq.data.enquiries || [] : [];
            const uqList = uq.ok ? uq.data.requests || [] : [];
            const dqList = dq.ok ? dq.data.enquiries || [] : [];
            setEnquiries(eqList);
            setUpgradeRequests(uqList);
            setDismissedEnquiries(dqList);
        } catch { /* silently ignore */ }
        finally { setRequestsLoading(false); }
    }, []);

    const loadProspects = useCallback(async () => {
        setProspectsLoading(true);
        try {
            const filters = { status: statusFilter };
            if (sourceFilter !== 'all') filters.prospectSource = sourceFilter;
            const r = await classApi.getProspects(filters);
            if (r.ok) setProspects(r.data.prospects || []);
        } catch { /* silently ignore */ }
        finally { setProspectsLoading(false); }
    }, [statusFilter, sourceFilter]);

    useEffect(() => { loadRequests(); }, [loadRequests]);
    useEffect(() => { loadProspects(); }, [loadProspects]);

    const handleEnrol = async enq => {
        if (!enq.classId) return;
        try {
            const r = await classApi.placeEnquiry(enq.id, enq.classId);
            if (r.ok) { toast.success(`${enq.firstName} enrolled`); loadRequests(); }
            else toast.error(r.data?.message || 'Failed to enrol');
        } catch { toast.error('Failed to enrol student'); }
    };

    const handleDismiss = enq => {
        const name = `${enq.firstName || ''} ${enq.lastName || ''}`.trim();
        setConfirmModal({
            title: 'Dismiss Enquiry',
            message: `Dismiss enquiry from ${name}? The enquiry will move to the dismissed section.`,
            variant: 'warning',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const r = await classApi.dismissEnquiry(enq.id);
                    if (r.ok) { toast.success('Enquiry dismissed'); loadRequests(); }
                    else toast.error(r.data?.message || 'Failed to dismiss');
                } catch { toast.error('Failed to dismiss enquiry'); }
            },
        });
    };

    const handleChange = enq => {
        if (onEnrolStudent) onEnrolStudent({ id: enq.studentId || enq.accountId, firstName: enq.firstName || '', lastName: enq.lastName || '' });
    };

    const handleResolve = async (id, decision) => {
        try {
            const r = await classApi.resolveUpgradeRequest(id, decision, '');
            if (r.ok) { toast.success(`Request ${decision}`); loadRequests(); }
            else toast.error(r.data?.message || 'Failed to update request');
        } catch { toast.error('Failed to update request'); }
    };

    const handleMarkLostInline = prospect => {
        setConfirmModal({
            title: 'Mark as Lost',
            message: `Mark ${prospect.firstName} ${prospect.lastName} as a lost lead?`,
            variant: 'warning',
            confirmText: 'Mark Lost',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    const r = await classApi.markProspectLost(prospect.id);
                    if (r.ok) { toast.success('Marked as lost'); loadProspects(); }
                    else toast.error(r.data?.message || 'Failed to update');
                } catch { toast.error('Failed to update'); }
            },
        });
    };

    const STATUS_CHIPS = [
        { value: 'active', label: 'Active' },
        { value: 'converted', label: 'Converted' },
        { value: 'lost', label: 'Lost' },
        { value: 'all', label: 'All' },
    ];

    const SOURCE_CHIPS = [
        { value: 'all', label: 'All sources' },
        { value: 'trial', label: 'Trial' },
        { value: 'walkin', label: 'Walk-in' },
        { value: 'enquiry', label: 'Enquiry' },
        { value: 'import', label: 'Import' },
        { value: 'manual', label: 'Manual' },
    ];

    return (
        <div>
            {/* Enrolment Enquiries */}
            <div className="class-students-requests-section">
                <div className="class-students-requests-divider">
                    <span>Enrolment Enquiries</span>
                    {enquiries.length > 0 && <span className="class-students-requests-count">{enquiries.length}</span>}
                </div>
                {requestsLoading ? (
                    <p className="class-students-requests-empty">Loading...</p>
                ) : enquiries.length === 0 ? (
                    <p className="class-students-requests-empty">No new enquiries — students interested in joining will appear here</p>
                ) : (
                    <div className="class-students-list">
                        {enquiries.map(enq => {
                            const initials = `${enq.firstName?.[0] || ''}${enq.lastName?.[0] || ''}`.toUpperCase() || '?';
                            return (
                                <div key={enq.id} className="class-students-row">
                                    <div className="class-students-avatar" aria-hidden="true">{initials}</div>
                                    <div className="class-students-info">
                                        <div className="class-students-name">
                                            {enq.firstName} {enq.lastName}
                                            {enq.waitlisted && <span className="class-students-class-tag class-students-class-tag--waitlist">Waitlist</span>}
                                        </div>
                                        <div className="class-students-meta">
                                            {enq.className ? <span className="class-students-class-tag">{enq.className}</span> : <span className="class-students-general-tag">General enquiry</span>}
                                            {enq.role && <> · {enq.role === 'leader' ? 'Leader' : 'Follower'}</>}
                                            {enq.email && <> · {enq.email}</>}
                                            {enq.phone && <> · {enq.phone}</>}
                                            {enq.experienceNotes && <> · {enq.experienceNotes}</>}
                                        </div>
                                    </div>
                                    <div className="class-students-actions">
                                        {enq.classId && (
                                            <button className="class-students-approve-btn" onClick={() => handleEnrol(enq)}>
                                                <Icon path={mdiCheck} size={0.8} /> Enrol
                                            </button>
                                        )}
                                        <button className="class-students-change-btn" onClick={() => handleChange(enq)} title="Dismiss enquiry and enrol in a different class">
                                            <Icon path={mdiSwapHorizontal} size={0.8} /> Change
                                        </button>
                                        <button className="class-students-decline-btn" onClick={() => handleDismiss(enq)}>
                                            <Icon path={mdiClose} size={0.8} /> Dismiss
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Upgrade Requests */}
            <div className="class-students-requests-section">
                <div className="class-students-requests-divider">
                    <span>Upgrade Requests</span>
                    {upgradeRequests.length > 0 && <span className="class-students-requests-count">{upgradeRequests.length}</span>}
                </div>
                {upgradeRequests.length === 0 ? (
                    <p className="class-students-requests-empty">No upgrade requests — students wanting to move between classes will appear here</p>
                ) : (
                    <div className="class-students-list">
                        {upgradeRequests.map(r => (
                            <div key={r.id} className="class-students-row">
                                <div className="class-students-info">
                                    <div className="class-students-name">Student: {r.studentProfileId}</div>
                                    <div className="class-students-meta">Class: {r.classId} · Note: {r.note || 'None'}</div>
                                </div>
                                <div className="class-students-actions">
                                    <button className="class-students-approve-btn" onClick={() => handleResolve(r.id, 'approved')}>
                                        <Icon path={mdiCheck} size={0.8} /> Approve
                                    </button>
                                    <button className="class-students-decline-btn" onClick={() => handleResolve(r.id, 'declined')}>
                                        <Icon path={mdiClose} size={0.8} /> Decline
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Dismissed Enquiries */}
            {dismissedEnquiries.length > 0 && (
                <div className="class-students-requests-section">
                    <button className="class-students-dismissed-toggle" onClick={() => setShowDismissed(prev => !prev)}>
                        <Icon path={showDismissed ? mdiChevronUp : mdiChevronDown} size={0.8} />
                        <span>Dismissed ({dismissedEnquiries.length})</span>
                    </button>
                    {showDismissed && (
                        <div className="class-students-list">
                            {dismissedEnquiries.map(enq => {
                                const initials = `${enq.firstName?.[0] || ''}${enq.lastName?.[0] || ''}`.toUpperCase() || '?';
                                return (
                                    <div key={enq.id} className="class-students-row class-students-row--dismissed">
                                        <div className="class-students-avatar" aria-hidden="true">{initials}</div>
                                        <div className="class-students-info">
                                            <div className="class-students-name">{enq.firstName} {enq.lastName}</div>
                                            <div className="class-students-meta">
                                                {enq.className && <span className="class-students-class-tag">{enq.className}</span>}
                                                {enq.email && <> · {enq.email}</>}
                                                {enq.phone && <> · {enq.phone}</>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Prospects Pipeline */}
            <div className="class-students-requests-section">
                <div className="class-students-requests-divider">
                    <span>Prospects Pipeline</span>
                    {prospectsLoading ? null : <span className="class-students-requests-count">{prospects.length}</span>}
                </div>

                {/* Status filter chips */}
                <div className="prospect-filter-row">
                    {STATUS_CHIPS.map(c => (
                        <button
                            key={c.value}
                            className={`class-students-filter-btn${statusFilter === c.value ? ' active' : ''}`}
                            onClick={() => setStatusFilter(c.value)}>
                            {c.label}
                        </button>
                    ))}
                </div>

                {/* Source filter chips */}
                <div className="prospect-filter-row">
                    {SOURCE_CHIPS.map(c => (
                        <button
                            key={c.value}
                            className={`class-students-filter-btn${sourceFilter === c.value ? ' active' : ''}`}
                            onClick={() => setSourceFilter(c.value)}>
                            {c.label}
                        </button>
                    ))}
                </div>

                {prospectsLoading ? (
                    <p className="class-students-requests-empty">Loading...</p>
                ) : prospects.length === 0 ? (
                    <p className="class-students-requests-empty">
                        {statusFilter === 'active'
                            ? 'No active prospects. Trial buyers, walk-ins, and enquiries appear here once captured.'
                            : 'No prospects match the selected filters.'}
                    </p>
                ) : (
                    <div className="class-students-list">
                        {prospects.map(p => {
                            const initials = `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`.toUpperCase() || '?';
                            return (
                                <div key={p.id} className="class-students-row">
                                    <div className="class-students-avatar" aria-hidden="true">{initials}</div>
                                    <div className="class-students-info">
                                        <div className="class-students-name">{p.firstName} {p.lastName}</div>
                                        <div className="class-students-meta">
                                            {(p.prospectSources || []).map(src => (
                                                <span key={src} className={`prospect-source-pill prospect-source-pill--${src}`}>
                                                    {PROSPECT_SOURCE_LABELS[src] || src}
                                                </span>
                                            ))}
                                            {p.leadSourceLabel && <> · {p.leadSourceLabel}</>}
                                            {p.trialDealName && <> · {p.trialDealName}</>}
                                            {p.email && <> · {p.email}</>}
                                        </div>
                                    </div>
                                    <div className="class-students-actions">
                                        <button className="class-students-view-prospect-btn" onClick={() => setSelectedProspect(p)}>
                                            <Icon path={mdiAccountDetails} size={0.8} /> View
                                        </button>
                                        {p.prospectStatus === 'active' && (
                                            <button className="class-students-decline-btn" onClick={() => handleMarkLostInline(p)}>
                                                <Icon path={mdiCloseCircle} size={0.8} /> Mark Lost
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedProspect && (
                <ProspectDetailModal
                    prospect={selectedProspect}
                    onClose={() => setSelectedProspect(null)}
                    onReload={() => { loadProspects(); setSelectedProspect(null); }}
                />
            )}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    confirmText={confirmModal.confirmText}
                    cancelText={confirmModal.cancelText}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// AttendanceHistoryModal — focused attendance-only modal for the Attendance tab
// ---------------------------------------------------------------------------
function AttendanceHistoryModal({ student, currentTerm, onClose }) {
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        if (!student?.id) return;
        setIsLoading(true);
        classApi
            .getAttendance(student.id)
            .then(r => {
                if (r.ok) setRecords(r.data.records || []);
            })
            .finally(() => setIsLoading(false));
    }, [student?.id]);

    const displayName = [student.firstName, student.lastName].filter(Boolean).join(" ") || "Student";

    // Filter ATTENDANCE RECORDS to those that fall within the current term's
    // date window. Naive Date comparison is fine here — both sides are full
    // ISO timestamps and we're checking record-membership, not term currency.
    // (Term currency lives on backend-supplied t.isCurrent.)
    const termStart = currentTerm?.startDate ? new Date(currentTerm.startDate) : null;
    const termEnd = currentTerm?.endDate ? new Date(currentTerm.endDate) : null;
    const fallbackCutoff = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        return d;
    }, []);

    const currentRecords = useMemo(
        () =>
            records.filter(r => {
                if (!r.date) return false;
                const d = new Date(r.date);
                if (termStart && termEnd) return d >= termStart && d <= termEnd;
                return d >= fallbackCutoff;
            }),
        [records, termStart, termEnd, fallbackCutoff]
    );

    const displayed = showAll ? records : currentRecords;
    const hasOlderRecords = records.length > currentRecords.length;
    const scopeLabel = currentTerm?.name || "the last 90 days";

    return (
        <BaseModal
            isOpen={true}
            onClose={onClose}
            title={displayName}
            footer={
                <button className="base-modal-cancel-btn" onClick={onClose}>
                    Close
                </button>
            }>
            <p className="class-students-attendance-history-scope">
                {currentTerm ? `Showing: ${currentTerm.name}` : "Showing: Last 90 days"}
            </p>
            {isLoading && <p className="class-students-loading">Loading attendance...</p>}
            {!isLoading && records.length === 0 && <p className="class-students-empty">No attendance records found</p>}
            {!isLoading && currentRecords.length === 0 && records.length > 0 && (
                <p className="class-students-empty">No sessions recorded for {scopeLabel}</p>
            )}
            {!isLoading && displayed.length > 0 && (
                <div className="class-students-attendance-history-list">
                    {displayed.map((rec, i) => {
                        const d = rec.date ? new Date(rec.date) : null;
                        const dateStr = d
                            ? d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                            : "—";
                        return (
                            <div key={i} className="class-students-attendance-history-row">
                                <Icon
                                    path={
                                        rec.isCancelled
                                            ? mdiCalendarRemove
                                            : rec.checkedIn
                                              ? mdiCheckCircle
                                              : mdiMinusCircleOutline
                                    }
                                    size={0.9}
                                    color={
                                        rec.isCancelled
                                            ? "var(--dl-color-purple-900)"
                                            : rec.checkedIn
                                              ? "var(--dl-color-greentick)"
                                              : "var(--dl-color-needs-action)"
                                    }
                                />
                                <span className="class-students-attendance-history-date">{dateStr}</span>
                                <span className="class-students-attendance-history-class">{rec.className}</span>
                                {rec.isCancelled ? (
                                    <span className="class-students-badge cancelled">
                                        Session cancelled — Make-up owed
                                    </span>
                                ) : (
                                    <>
                                        {rec.isMakeup && <span className="class-students-badge makeup">Make-up</span>}
                                        {rec.isWalkIn && <span className="class-students-badge walkin">Walk-in</span>}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {!isLoading && hasOlderRecords && (
                <button className="class-students-attendance-history-toggle" onClick={() => setShowAll(v => !v)}>
                    {showAll ? `Show ${scopeLabel} only` : `Show all history (${records.length} sessions)`}
                </button>
            )}
        </BaseModal>
    );
}

// ---------------------------------------------------------------------------
// AttendanceTab
// ---------------------------------------------------------------------------
const getAttendanceBadge = pct => {
    if (pct >= 75) return "high";
    if (pct >= 40) return "mid";
    return "low";
};

function AttendanceTab({ orgId }) {
    const [report, setReport] = useState(null);
    const [terms, setTerms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTermId, setSelectedTermId] = useState("");
    const [selectedClassId, setSelectedClassId] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sortField, setSortField] = useState("attendancePct");
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [search, setSearch] = useState("");

    const loadReport = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await classApi.getAttendanceReport({
                termId: selectedTermId || undefined,
                classId: selectedClassId || undefined,
                from: dateFrom || undefined,
                to: dateTo || undefined,
            });
            if (r.ok) {
                setReport(r.data.students || []);
                if (r.data.terms?.length) {
                    setTerms(r.data.terms);
                    // Auto-select the current term on first load only
                    if (!selectedTermId) {
                        const current = r.data.terms.find(t => t.isCurrent);
                        if (current) setSelectedTermId(current.id);
                    }
                }
                if (r.data.classes?.length) setClasses(r.data.classes);
            } else {
                toast.error(r.data?.message || "Failed to load attendance report");
                setReport([]);
            }
        } catch {
            toast.error("Failed to load attendance report");
            setReport([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedTermId, selectedClassId, dateFrom, dateTo]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const handleSort = field => {
        if (sortField === field) {
            setSortDirection(d => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection(field === "attendancePct" ? "asc" : "asc");
        }
    };

    const rows = (Array.isArray(report) ? report : []).filter(
        r => !search || r.displayName?.toLowerCase().includes(search.toLowerCase())
    );

    const sorted = [...rows].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
    });

    const handleExportCsv = () => {
        if (!sorted.length) return;
        const headers = ["Student", "Classes", "Sessions Attended", "Total Sessions", "Attendance %"];
        const dataRows = sorted.map(r => [
            r.displayName,
            (r.classNames || []).join("; "),
            r.sessionsAttended,
            r.totalSessions,
            `${r.attendancePct}%`,
        ]);
        const csv = [headers, ...dataRows]
            .map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `student-attendance-${selectedTermId || selectedClassId || "all"}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return null;
        return <Icon path={sortDirection === "asc" ? mdiChevronUp : mdiChevronDown} size={0.8} />;
    };

    return (
        <div className="class-students-attendance-panel">
            <div className="class-students-attendance-filters">
                <select
                    className="class-students-attendance-select"
                    value={selectedTermId}
                    onChange={e => setSelectedTermId(e.target.value)}>
                    <option value="">All Terms</option>
                    {terms.map(t => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
                <select
                    className="class-students-attendance-select"
                    value={selectedClassId}
                    onChange={e => setSelectedClassId(e.target.value)}>
                    <option value="">All Classes</option>
                    {classes.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <input
                    type="date"
                    className="class-students-attendance-date"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    title="From date"
                />
                <input
                    type="date"
                    className="class-students-attendance-date"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    title="To date"
                />
                <input
                    type="text"
                    className="class-students-search"
                    placeholder="Search by name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {(selectedTermId || selectedClassId || dateFrom || dateTo || search) && (
                    <button
                        className="class-students-attendance-clear-btn"
                        onClick={() => {
                            setSelectedTermId("");
                            setSelectedClassId("");
                            setDateFrom("");
                            setDateTo("");
                            setSearch("");
                        }}>
                        Clear filters
                    </button>
                )}
                <button className="class-students-export-btn" onClick={handleExportCsv} disabled={!sorted.length}>
                    <Icon path={mdiDownload} size={0.85} /> Export CSV
                </button>
            </div>

            {isLoading && <p className="class-students-loading">Loading attendance data...</p>}

            {!isLoading && sorted.length === 0 && (
                <p className="class-students-empty">No attendance data available for the selected filters</p>
            )}

            {!isLoading && sorted.length > 0 && (
                <div className="class-students-attendance-table-wrapper">
                    <table className="class-students-attendance-table">
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort("displayName")}
                                    className="class-students-attendance-th-sortable">
                                    Student <SortIcon field="displayName" />
                                </th>
                                <th className="class-students-attendance-th">Classes</th>
                                <th
                                    onClick={() => handleSort("sessionsAttended")}
                                    className="class-students-attendance-th-sortable">
                                    Sessions <SortIcon field="sessionsAttended" />
                                </th>
                                <th
                                    onClick={() => handleSort("attendancePct")}
                                    className="class-students-attendance-th-sortable">
                                    Attendance <SortIcon field="attendancePct" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row, i) => (
                                <tr
                                    key={i}
                                    onClick={() =>
                                        setSelectedStudent({
                                            id: row.id,
                                            firstName: row.firstName,
                                            lastName: row.lastName,
                                            source: row.studentProfileId ? "student-profile" : "account",
                                        })
                                    }
                                    className="class-students-attendance-row">
                                    <td>{row.displayName}</td>
                                    <td className="class-students-attendance-classes-cell">
                                        {!row.classNames
                                            ? "—"
                                            : row.classNames.length === 1
                                              ? row.classNames[0]
                                              : `${row.classNames.length} classes`}
                                    </td>
                                    <td>
                                        {row.sessionsAttended}/{row.totalSessions}
                                    </td>
                                    <td className="class-students-attendance-bar-cell">
                                        <div className="class-students-attendance-bar-wrap">
                                            <div className="class-students-attendance-bar-bg">
                                                <div
                                                    className="class-students-attendance-bar-fill"
                                                    style={{ width: `${Math.min(row.attendancePct || 0, 100)}%` }}
                                                />
                                            </div>
                                            <span
                                                className={`class-students-attendance-badge ${getAttendanceBadge(row.attendancePct || 0)}`}>
                                                {row.attendancePct ?? 0}%
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedStudent && (
                <AttendanceHistoryModal
                    student={selectedStudent}
                    currentTerm={terms.find(t => t.id === selectedTermId) || null}
                    onClose={() => setSelectedStudent(null)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// HistoryTab — school-wide past & cancelled enrolments
// ---------------------------------------------------------------------------
function HistoryTab({ orgId }) {
    const [enrolments, setEnrolments] = useState(null);
    const [terms, setTerms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState("");
    const [selectedTermId, setSelectedTermId] = useState("");
    const [selectedClassId, setSelectedClassId] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [sortField, setSortField] = useState("termEndDate");
    const [sortDirection, setSortDirection] = useState("desc");
    const [search, setSearch] = useState("");

    const loadHistory = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await classApi.getEnrolmentHistory({
                termId: selectedTermId || undefined,
                classId: selectedClassId || undefined,
                status: selectedStatus || undefined,
            });
            if (r.ok) {
                setEnrolments(r.data.enrolments || []);
                if (r.data.terms?.length) setTerms(r.data.terms);
                if (r.data.classes?.length) setClasses(r.data.classes);
            } else {
                toast.error(r.data?.message || "Failed to load enrolment history");
                setEnrolments([]);
            }
        } catch {
            toast.error("Failed to load enrolment history");
            setEnrolments([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedTermId, selectedClassId, selectedStatus]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleSort = field => {
        if (sortField === field) {
            setSortDirection(d => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return null;
        return <Icon path={sortDirection === "asc" ? mdiChevronUp : mdiChevronDown} size={0.8} />;
    };

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Client-side name search on loaded data
    const rows = (Array.isArray(enrolments) ? enrolments : []).filter(
        r => !search || r.displayName?.toLowerCase().includes(search.toLowerCase())
    );

    const sorted = [...rows].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (!aVal && !bVal) return 0;
        if (!aVal) return 1;
        if (!bVal) return -1;
        if (typeof aVal === "string") {
            aVal = aVal.toLowerCase();
            bVal = (bVal || "").toLowerCase();
        }
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
    });

    const handleExportCsv = () => {
        if (!sorted.length) return;
        const headers = ["Student", "Term", "Class", "Class time", "Action date", "Status"];
        const dataRows = sorted.map(r => {
            const refunded = r.status === "cancelled" && (r.refundAmount || 0) > 0;
            return [
                r.displayName,
                r.termName,
                r.className,
                r.dayOfWeek != null ? `${dayNames[r.dayOfWeek]} ${r.startTime || ""}` : "",
                r.cancelledAt ? new Date(r.cancelledAt).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "",
                refunded ? `Refunded ${formatCurrencyFromMinor(r.refundAmount)}` : r.status === "cancelled" ? "Removed" : r.status === "inactive" ? "Inactive" : "Past",
            ];
        });
        const csv = [headers, ...dataRows]
            .map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `enrolment-history.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // Derive available years from terms (using startDate), sorted descending
    const availableYears = [...new Set(
        terms
            .map(t => t.startDate ? new Date(t.startDate).getFullYear() : null)
            .filter(Boolean)
    )].sort((a, b) => b - a);

    // Terms visible in the term picker: filtered to the selected year if one is chosen
    const termsForYear = selectedYear
        ? terms.filter(t => t.startDate && new Date(t.startDate).getFullYear() === Number(selectedYear))
        : terms;

    const handleYearChange = year => {
        setSelectedYear(year);
        setSelectedTermId(""); // clear term when year changes
    };

    const hasFilters = selectedYear || selectedTermId || selectedClassId || selectedStatus || search;

    return (
        <div className="class-students-attendance-panel">
            <div className="class-students-attendance-filters class-students-history-filters">
                <div className="class-students-attendance-filters-row">
                    <select
                        className="class-students-attendance-select"
                        value={selectedYear}
                        onChange={e => handleYearChange(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <select
                        className="class-students-attendance-select"
                        value={selectedTermId}
                        onChange={e => setSelectedTermId(e.target.value)}
                        disabled={!selectedYear}>
                        <option value="">{selectedYear ? "All Terms" : "Select a year first"}</option>
                        {termsForYear.map(t => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="class-students-attendance-select"
                        value={selectedClassId}
                        onChange={e => setSelectedClassId(e.target.value)}>
                        <option value="">All Classes</option>
                        {classes.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="class-students-attendance-select"
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(e.target.value)}>
                        <option value="">All Statuses</option>
                        <option value="past">Past</option>
                        <option value="cancelled">Removed</option>
                    </select>
                </div>
                <div className="class-students-attendance-filters-row">
                    <div className="class-students-search-wrap">
                        <input
                            type="text"
                            className="class-students-search class-students-search--history"
                            placeholder="Search by name..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className="class-students-search-clear"
                                onClick={() => setSearch("")}
                                aria-label="Clear search">
                                <Icon path={mdiClose} size={0.7} />
                            </button>
                        )}
                    </div>
                    <button
                        className="class-students-attendance-clear-btn"
                        style={{ visibility: hasFilters ? "visible" : "hidden" }}
                        onClick={() => {
                            setSelectedYear("");
                            setSelectedTermId("");
                            setSelectedClassId("");
                            setSelectedStatus("");
                            setSearch("");
                        }}>
                        Clear filters
                    </button>
                    <button className="class-students-export-btn" onClick={handleExportCsv} disabled={!sorted.length}>
                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Icon path={mdiDownload} size={0.85} /> Export CSV
                        </span>
                    </button>
                </div>
            </div>

            {isLoading && <p className="class-students-loading">Loading history...</p>}

            {!isLoading && sorted.length === 0 && (
                <p className="class-students-empty">No enrolment history found for the selected filters</p>
            )}

            {!isLoading && sorted.length > 0 && (
                <div className="class-students-attendance-table-wrapper">
                    <table className="class-students-attendance-table">
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort("displayName")}
                                    className="class-students-attendance-th-sortable">
                                    Student <SortIcon field="displayName" />
                                </th>
                                <th
                                    onClick={() => handleSort("termEndDate")}
                                    className="class-students-attendance-th-sortable">
                                    Term <SortIcon field="termEndDate" />
                                </th>
                                <th
                                    onClick={() => handleSort("className")}
                                    className="class-students-attendance-th-sortable">
                                    Class <SortIcon field="className" />
                                </th>
                                <th className="class-students-attendance-th">Class time</th>
                                <th
                                    onClick={() => handleSort("cancelledAt")}
                                    className="class-students-attendance-th-sortable">
                                    Action date <SortIcon field="cancelledAt" />
                                </th>
                                <th className="class-students-attendance-th">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row, i) => {
                                // A removed enrolment that also carries a refund on its covering payment
                                // is shown as "Refunded" (turquoise), distinct from a plain "Removed".
                                const isRefunded = row.status === "cancelled" && (row.refundAmount || 0) > 0;
                                const statusClass = isRefunded
                                    ? "class-students-badge--pay-refunded"
                                    : row.status === "cancelled"
                                      ? "cancelled"
                                      : row.status === "inactive"
                                        ? "inactive"
                                        : "enrolled";
                                const statusLabel = isRefunded
                                    ? "Refunded"
                                    : row.status === "cancelled"
                                      ? "Removed"
                                      : row.status === "inactive"
                                        ? "Inactive"
                                        : "Past";
                                const classTime =
                                    row.dayOfWeek != null ? `${dayNames[row.dayOfWeek]} ${row.startTime || ""}` : "—";
                                // Action date = when the removal/refund happened (cancelledAt). Blank for
                                // natural "Past" rows (term simply ended — no action was taken).
                                const actionDate = row.cancelledAt
                                    ? new Date(row.cancelledAt).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
                                    : "—";
                                return (
                                    <tr key={i} className="class-students-history-row">
                                        <td>{row.displayName}</td>
                                        <td className="class-students-history-term-cell">{row.termName || "—"}</td>
                                        <td>{row.className || "—"}</td>
                                        <td>{classTime}</td>
                                        <td>{actionDate}</td>
                                        <td>
                                            <span className={`class-students-badge ${statusClass}`}>{statusLabel}</span>
                                            {isRefunded && (
                                                <span className="class-students-history-refund-amount">
                                                    {formatCurrencyFromMinor(row.refundAmount)}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// assignColumns — helper for PrintListTab
// ---------------------------------------------------------------------------
function assignColumns(enrolments) {
    const leaders = [];
    const followers = [];
    const unassigned = enrolments.filter(e => !e.role);
    enrolments.filter(e => e.role === "leader").forEach(e => leaders.push({ ...e, displaySuffix: "" }));
    enrolments.filter(e => e.role === "follower").forEach(e => followers.push({ ...e, displaySuffix: "" }));
    for (const e of unassigned) {
        if (leaders.length <= followers.length) {
            leaders.push({ ...e, displaySuffix: " ?" });
        } else {
            followers.push({ ...e, displaySuffix: " ?" });
        }
    }
    return { leaders, followers };
}

// ---------------------------------------------------------------------------
// PrintListTab — landscape roll-call print list for admins
// ---------------------------------------------------------------------------
function PrintListTab({ orgId }) {
    const [classType, setClassType] = useState("regular");
    const [terms, setTerms] = useState([]);
    const [shortCourses, setShortCourses] = useState([]);
    const [selectedTermId, setSelectedTermId] = useState("");
    const [selectedCourseId, setSelectedCourseId] = useState("");
    const [selectedStyle, setSelectedStyle] = useState("");
    const [selectedDay, setSelectedDay] = useState("");
    const [selectedRole, setSelectedRole] = useState("all");
    const [classDefs, setClassDefs] = useState([]);
    const [enrolmentMap, setEnrolmentMap] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    // Load terms and short courses on mount; auto-select the active term
    useEffect(() => {
        Promise.all([classApi.getTerms(), classApi.getShortCourses()])
            .then(([termsRes, scRes]) => {
                if (termsRes.ok) {
                    const loadedTerms = termsRes.data.terms || [];
                    setTerms(loadedTerms);
                    const activeTerm =
                        loadedTerms.find(t => t.isCurrent) || loadedTerms.find(t => !t.isPast);
                    if (activeTerm) setSelectedTermId(activeTerm.id);
                }
                if (scRes.ok) setShortCourses(scRes.data.shortCourses || []);
            })
            .catch(() => {});
    }, []);

    // When term changes (regular classes) — fetch class definitions
    useEffect(() => {
        if (classType !== "regular") return;
        setClassDefs([]);
        setEnrolmentMap({});
        if (!selectedTermId) return;
        setIsLoading(true);
        classApi
            .getClasses(selectedTermId)
            .then(res => {
                if (res.ok) setClassDefs(res.data.classes || []);
                else toast.error(res.data?.message || "Failed to load classes");
            })
            .catch(() => {
                toast.error("Failed to load classes");
            })
            .finally(() => setIsLoading(false));
    }, [selectedTermId, classType]);

    // Load enrolments for all active class definitions
    useEffect(() => {
        if (classType !== "regular" || !classDefs.length) return;
        const fetchAll = async () => {
            const map = {};
            await Promise.all(
                classDefs.map(async cls => {
                    const r = await classApi.getEnrolments(cls.id);
                    if (r.ok) {
                        map[cls.id] = (r.data.enrolments || []).filter(e => e.status !== "cancelled");
                    } else {
                        map[cls.id] = [];
                    }
                })
            );
            setEnrolmentMap(map);
        };
        fetchAll().catch(() => {});
    }, [classDefs, classType]);

    // Load short-course enrolments when course selected
    useEffect(() => {
        if (classType !== "short-course") return;
        if (!selectedCourseId) {
            setEnrolmentMap({});
            setClassDefs([]);
            return;
        }
        setIsLoading(true);
        classApi
            .getShortCourseEnrolments(selectedCourseId)
            .then(r => {
                if (r.ok) {
                    const sc = shortCourses.find(s => s.id === selectedCourseId);
                    if (sc) {
                        setClassDefs([sc]);
                        setEnrolmentMap({ [sc.id]: (r.data.enrolments || []).filter(e => e.status !== "cancelled") });
                    }
                } else {
                    toast.error(r.data?.message || "Failed to load enrolments");
                }
            })
            .catch(() => {
                toast.error("Failed to load enrolments");
            })
            .finally(() => setIsLoading(false));
    }, [selectedCourseId, classType, shortCourses]);

    // Compute the filtered class list for preview
    const filteredClasses = useMemo(() => {
        return classDefs.filter(cls => {
            if (cls.isCancelled) return false;
            if (selectedStyle && cls.style !== selectedStyle) return false;
            if (selectedDay !== "" && cls.dayOfWeek !== parseInt(selectedDay, 10)) return false;
            return true;
        });
    }, [classDefs, selectedStyle, selectedDay]);

    // Available styles derived from current classDefs
    const availableStyles = useMemo(() => {
        const seen = new Set();
        return classDefs.map(c => c.style).filter(s => s && !seen.has(s) && seen.add(s));
    }, [classDefs]);

    // Available days derived from current classDefs
    const availableDays = useMemo(() => {
        const seen = new Set();
        return classDefs
            .map(c => c.dayOfWeek)
            .filter(d => d != null && !seen.has(d) && seen.add(d))
            .sort((a, b) => a - b);
    }, [classDefs]);

    const handlePrint = () => {
        const preview = document.getElementById("print-list-preview");
        if (!preview) {
            window.print();
            return;
        }

        // Clone preview as a direct child of <body> so it sits at top: 0 with no
        // inherited spacing from the app shell. Everything else is hidden via CSS class.
        const clone = document.createElement("div");
        clone.id = "print-list-body-clone";
        clone.innerHTML = preview.innerHTML;
        document.body.appendChild(clone);
        document.body.classList.add("printing-class-list");

        const cleanup = () => {
            document.body.removeChild(clone);
            document.body.classList.remove("printing-class-list");
            window.removeEventListener("afterprint", cleanup);
        };
        window.addEventListener("afterprint", cleanup);
        window.print();
    };

    const handleClearFilters = () => {
        setSelectedStyle("");
        setSelectedDay("");
        setSelectedRole("all");
    };

    const hasFilters = selectedStyle || selectedDay !== "" || selectedRole !== "all";

    return (
        <div className="print-list-tab">
            {/* ── Filter bar ── */}
            <div className="class-students-attendance-filters print-list-tab-filters">
                {/* Type */}
                <select
                    className="class-students-attendance-select"
                    value={classType}
                    onChange={e => {
                        setClassType(e.target.value);
                        setSelectedTermId("");
                        setSelectedCourseId("");
                        setSelectedStyle("");
                        setSelectedDay("");
                        setClassDefs([]);
                        setEnrolmentMap({});
                    }}>
                    <option value="regular">Regular Classes</option>
                    <option value="short-course">Short Courses</option>
                </select>

                {/* Term (regular) or Course (short-course) */}
                {classType === "regular" ? (
                    <select
                        className="class-students-attendance-select"
                        value={selectedTermId}
                        onChange={e => setSelectedTermId(e.target.value)}>
                        <option value="">Select Term</option>
                        {terms
                            .filter(t => !t.isPast)
                            .map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        {/* 180-day "recently past" UX heuristic — naive UTC compare is acceptable
                           here, this is a rough cutoff for the dropdown, not currency classification. */}
                        {terms.some(
                            t => t.isPast && new Date(t.endDate) >= new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)
                        ) && (
                            <optgroup label="Past Terms">
                                {terms
                                    .filter(
                                        t =>
                                            t.isPast &&
                                            new Date(t.endDate) >= new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)
                                    )
                                    .map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                            </optgroup>
                        )}
                    </select>
                ) : (
                    <select
                        className="class-students-attendance-select"
                        value={selectedCourseId}
                        onChange={e => setSelectedCourseId(e.target.value)}>
                        <option value="">Select Course</option>
                        {shortCourses.map(sc => (
                            <option key={sc.id} value={sc.id}>
                                {sc.name}
                            </option>
                        ))}
                    </select>
                )}

                {/* Dance Style */}
                <select
                    className="class-students-attendance-select"
                    value={selectedStyle}
                    onChange={e => setSelectedStyle(e.target.value)}
                    disabled={!classDefs.length}>
                    <option value="">All Styles</option>
                    {availableStyles.map(s => (
                        <option key={s} value={s}>
                            {getDanceStyleLabel(s)}
                        </option>
                    ))}
                </select>

                {/* Day of Week */}
                <select
                    className="class-students-attendance-select"
                    value={selectedDay}
                    onChange={e => setSelectedDay(e.target.value)}
                    disabled={!classDefs.length}>
                    <option value="">All Days</option>
                    {availableDays.map(d => (
                        <option key={d} value={String(d)}>
                            {DAY_NAMES[d]}
                        </option>
                    ))}
                </select>

                {/* Role */}
                <select
                    className="class-students-attendance-select"
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    disabled={!classDefs.length}>
                    <option value="all">All Roles</option>
                    <option value="leaders">Leaders Only</option>
                    <option value="followers">Followers Only</option>
                </select>

                {hasFilters && (
                    <button className="class-students-attendance-clear-btn" onClick={handleClearFilters}>
                        Clear filters
                    </button>
                )}

                {filteredClasses.length > 0 && (
                    <button className="print-list-tab-print-btn" onClick={handlePrint}>
                        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Icon path={mdiPrinter} size={0.8} />
                            Print
                        </span>
                    </button>
                )}
            </div>

            {/* ── Loading / empty states ── */}
            {isLoading && <p className="class-students-loading">Loading...</p>}
            {!isLoading && !selectedTermId && classType === "regular" && (
                <p className="class-students-empty">Select a term to generate a print list.</p>
            )}
            {!isLoading && !selectedCourseId && classType === "short-course" && (
                <p className="class-students-empty">Select a short course to generate a print list.</p>
            )}
            {!isLoading && (selectedTermId || selectedCourseId) && filteredClasses.length === 0 && (
                <p className="class-students-empty">No classes match the selected filters.</p>
            )}

            {/* ── Print preview ── */}
            {!isLoading && filteredClasses.length > 0 && (
                <div className="print-list-tab-preview" id="print-list-preview">
                    <div className="print-list-tab-legend">
                        <span className="print-list-tab-legend-item print-list-tab-legend-item--confirmed">Enrolled</span>
                        <span className="print-list-tab-legend-item print-list-tab-legend-item--unconfirmed">Unconfirmed</span>
                    </div>
                    <div className="print-list-tab-grid">
                        {filteredClasses.map(cls => {
                            const rawEnrolments = enrolmentMap[cls.id] || [];
                            const filtered =
                                selectedRole === "leaders"
                                    ? rawEnrolments.filter(e => e.role === "leader")
                                    : selectedRole === "followers"
                                      ? rawEnrolments.filter(e => e.role === "follower")
                                      : rawEnrolments;

                            const { leaders, followers } = assignColumns(filtered);
                            const totalCount = leaders.length + followers.length;
                            const leaderCount = leaders.filter(e => !e.displaySuffix).length;
                            const followerCount = followers.filter(e => !e.displaySuffix).length;

                            return (
                                <div key={cls.id} className="print-list-tab-class-block">
                                    <div className="print-list-tab-class-header">
                                        <span className="print-list-tab-class-name">{cls.name}</span>
                                    </div>
                                    <div className="print-list-tab-columns">
                                        {/* Leaders column */}
                                        <div className="print-list-tab-col">
                                            <div className="print-list-tab-col-header">Leaders</div>
                                            <ul className="print-list-tab-names">
                                                {leaders.map((e, idx) => (
                                                    <li key={e.id || idx} className={`print-list-tab-name print-list-tab-name--${e.status === "confirmed" ? "confirmed" : e.status === "unconfirmed" ? "unconfirmed" : "other"}`}>
                                                        {e.student
                                                            ? `${e.student.firstName} ${e.student.lastName}`
                                                            : "Student"}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        {/* Followers column */}
                                        <div className="print-list-tab-col">
                                            <div className="print-list-tab-col-header">Followers</div>
                                            <ul className="print-list-tab-names">
                                                {followers.map((e, idx) => (
                                                    <li key={e.id || idx} className={`print-list-tab-name print-list-tab-name--${e.status === "confirmed" ? "confirmed" : e.status === "unconfirmed" ? "unconfirmed" : "other"}`}>
                                                        {e.student
                                                            ? `${e.student.firstName} ${e.student.lastName}`
                                                            : "Student"}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="print-list-tab-totals">
                                        {leaderCount} Leaders · {followerCount} Followers · {totalCount} Total
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// StudentDetailModal
// ---------------------------------------------------------------------------
function StudentDetailModal({ student, onClose, orgId, offersRefunds = false, onEnrol, onRequestPlaceOnBreak, onReactivate, onRequestRemove }) {
    const [history, setHistory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Attendance state
    const [attendance, setAttendance] = useState(null);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [expandedClasses, setExpandedClasses] = useState(new Set());
    // Remove enrolment state — a plain remove with NO refund (refunds are a separate action).
    const [removingEnrolment, setRemovingEnrolment] = useState(null); // enrolment object being removed
    const [isRemoving, setIsRemoving] = useState(false);
    // Refund enrolment state — the dedicated "Refund" action (gated on offersRefunds).
    // Partial refund keeps the student enrolled; a full refund cancels the enrolment.
    const [refundingEnrolment, setRefundingEnrolment] = useState(null);
    const [refundInput, setRefundInput] = useState(""); // dollars
    const [isRefunding, setIsRefunding] = useState(false);
    // Status change state
    const [statusChangingId, setStatusChangingId] = useState(null); // enrolment id currently being updated
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isSendingInvite] = useState(false);
    const [modalPhotoUrl, setModalPhotoUrl] = useState(null);
    const [showInviteEmailReview, setShowInviteEmailReview] = useState(false);
    const [isConfirmingInvite, setIsConfirmingInvite] = useState(false);

    const openInviteEmailReview = () => {
        setShowInviteEmailReview(true);
    };

    const handleConfirmInviteSend = async () => {
        setIsConfirmingInvite(true);
        try {
            const r = await classApi.sendInvite(student.id);
            if (r.ok) toast.success("Invite email sent");
            else toast.error(r.data?.message || "Failed to send invite");
        } catch {
            toast.error("Failed to send invite");
        } finally {
            setIsConfirmingInvite(false);
            setShowInviteEmailReview(false);
        }
    };

    const loadHistory = async () => {
        setIsLoading(true);
        try {
            const r = await classApi.getStudentHistory(student.id);
            if (r.ok) setHistory(r.data);
            else toast.error("Failed to load student history");
        } catch {
            toast.error("Failed to load student history");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!student) return;
        loadHistory();
        // Load attendance alongside history
        setAttendanceLoading(true);
        setExpandedClasses(new Set());
        classApi
            .getAttendance(student.id)
            .then(r => {
                if (r.ok) setAttendance(r.data);
                else setAttendance(null);
            })
            .catch(() => setAttendance(null))
            .finally(() => setAttendanceLoading(false));
    }, [student]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!student.avatarKey) {
            setModalPhotoUrl(null);
            return;
        }
        const profileId = student.id;
        const cacheKey =
            student.studentPhotoVersion != null
                ? `student_${profileId}_v${student.studentPhotoVersion}`
                : `student_account_${student.avatarKey}`;

        const cached = getCachedImage(cacheKey);
        if (cached) {
            setModalPhotoUrl(base64ToBlobUrl(cached));
            return;
        }

        let cancelled = false;
        getOrFetchImage(cacheKey, "profile", () =>
            classApi
                .getStudentPhotoBase64(profileId)
                .then(res => (res.ok && res.data?.imageBase64 ? res.data.imageBase64 : null))
        ).then(base64 => {
            if (!cancelled && base64) setModalPhotoUrl(base64ToBlobUrl(base64));
        });

        return () => {
            cancelled = true;
        };
    }, [student]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStatusChange = async (enrolmentId, newStatus) => {
        setStatusChangingId(enrolmentId);
        try {
            const r = await classApi.updateEnrolmentStatus(enrolmentId, newStatus);
            if (r.ok) {
                toast.success(newStatus === "confirmed" ? "Confirmed" : "Status updated");
                await loadHistory();
            } else {
                toast.error(r.data?.message || "Failed to update status");
            }
        } catch {
            toast.error("Failed to update status");
        } finally {
            setStatusChangingId(null);
        }
    };

    const handleToggleInactive = () => {
        if (student.isInactive) {
            onReactivate?.();
        } else {
            onRequestPlaceOnBreak?.();
        }
    };

    const handleRemoveClick = enrolment => {
        setRemovingEnrolment(enrolment);
    };

    const handleConfirmRemove = async () => {
        if (!removingEnrolment) return;
        setIsRemoving(true);
        try {
            // Plain remove — never issues a refund (refunds go through the Refund action).
            const r = await classApi.removeEnrolment(removingEnrolment.id, 0, false);
            if (r.ok) {
                toast.success("Removed from class.");
                setRemovingEnrolment(null);
                await loadHistory();
            } else {
                toast.error(r.data?.message || "Failed to remove enrolment");
            }
        } catch {
            toast.error("Failed to remove enrolment");
        } finally {
            setIsRemoving(false);
        }
    };

    // Remaining refundable for the enrolment being refunded (dollars), from the enrolment's
    // own payment fields (paymentAmount / refundAmount, both cents).
    const refundRemainingCents = refundingEnrolment
        ? (refundingEnrolment.paymentAmount || 0) - (refundingEnrolment.refundAmount || 0)
        : 0;

    const handleRefundClick = enrolment => {
        setRefundingEnrolment(enrolment);
        const remaining = (enrolment.paymentAmount || 0) - (enrolment.refundAmount || 0);
        setRefundInput((remaining / 100).toFixed(2)); // default to a full refund
    };

    const handleConfirmRefund = async () => {
        if (!refundingEnrolment) return;
        const refundCents = Math.round(parseFloat(refundInput) * 100);
        if (!refundCents || refundCents <= 0) {
            toast.error("Enter a refund amount greater than $0");
            return;
        }
        if (refundCents > refundRemainingCents) {
            toast.error(`Refund cannot exceed the remaining ${formatCurrencyFromMinor(refundRemainingCents)}`);
            return;
        }
        setIsRefunding(true);
        try {
            const r = await classApi.refundEnrolment(refundingEnrolment.id, refundCents);
            if (r.ok) {
                toast.success(
                    r.data.enrolmentCancelled
                        ? `Full refund of ${formatCurrencyFromMinor(refundCents)} issued. Student removed from the class.`
                        : `Partial refund of ${formatCurrencyFromMinor(refundCents)} issued. Student stays enrolled.`
                );
                setRefundingEnrolment(null);
                await loadHistory();
            } else {
                toast.error(r.data?.message || "Failed to issue refund");
            }
        } catch {
            toast.error("Failed to issue refund");
        } finally {
            setIsRefunding(false);
        }
    };

    if (!student) return null;

    // Active section = current-term enrolments PLUS future-term enrolments (e.g.
    // a rolled-over next-term reservation). The active render already badges a
    // future-term row as "Rolled over — term not open yet", so both belong here.
    const currentEnrolments = history?.enrolments?.filter(e => e.isCurrent || e.isFutureTerm) || [];
    // Past = neither current nor future. Glimpse only the most-recent past term
    // (the one that just finished) — full history is in the History tab.
    const pastEnrolments = history?.enrolments?.filter(e => !e.isCurrent && !e.isFutureTerm) || [];

    const mostRecentTermDate = pastEnrolments.reduce(
        (max, e) => ((e.termStartDate || "") > max ? e.termStartDate : max),
        ""
    );
    const recentPastEnrolments = mostRecentTermDate
        ? pastEnrolments.filter(e => e.termStartDate === mostRecentTermDate)
        : [];

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Remove confirmation view — a plain remove. No refund happens here (use the Refund action).
    if (removingEnrolment) {
        return (
            <BaseModal
                isOpen
                onClose={() => setRemovingEnrolment(null)}
                title="Remove from Class"
                icon={mdiTrashCanOutline}
                maxWidth={480}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setRemovingEnrolment(null)}>
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn"
                            style={{ background: "var(--dl-color-danger)" }}
                            onClick={handleConfirmRemove}
                            disabled={isRemoving}>
                            {isRemoving ? "Removing..." : "Confirm Remove"}
                        </button>
                    </>
                }>
                <p style={{ marginBottom: 12 }}>
                    Remove{" "}
                    <strong>
                        {student.firstName} {student.lastName}
                    </strong>{" "}
                    from <strong>{removingEnrolment.className}</strong>?
                </p>
                <p className="class-students-meta">
                    This removes the student from the class. No money is refunded.
                    {offersRefunds ? " To return money, use the Refund button instead." : ""}
                </p>
            </BaseModal>
        );
    }

    // Refund view — partial keeps the student enrolled; a full refund removes them.
    if (refundingEnrolment) {
        const previewCents = Math.round(parseFloat(refundInput || "0") * 100);
        const isFullRefund = previewCents > 0 && previewCents >= refundRemainingCents;
        return (
            <BaseModal
                isOpen
                onClose={() => setRefundingEnrolment(null)}
                title="Issue Refund"
                icon={mdiCashRefund}
                maxWidth={480}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setRefundingEnrolment(null)}>
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={handleConfirmRefund}
                            disabled={isRefunding}>
                            {isRefunding ? "Processing..." : "Issue Refund"}
                        </button>
                    </>
                }>
                <p style={{ marginBottom: 12 }}>
                    Refund{" "}
                    <strong>
                        {student.firstName} {student.lastName}
                    </strong>{" "}
                    for <strong>{refundingEnrolment.className}</strong>.
                </p>
                <div className="class-students-refund-panel">
                    <div className="class-students-refund-row">
                        <span className="class-students-refund-label">Remaining refundable</span>
                        <span className="class-students-refund-value">
                            {formatCurrencyFromMinor(refundRemainingCents)}
                        </span>
                    </div>
                    <div className="class-students-refund-amount-row" style={{ marginTop: 12 }}>
                        <span className="class-students-refund-currency">$</span>
                        <input
                            className="class-students-refund-amount-input"
                            type="number"
                            min="0"
                            step="1"
                            max={(refundRemainingCents / 100).toFixed(2)}
                            value={refundInput}
                            onChange={e => setRefundInput(e.target.value)}
                        />
                    </div>
                    <p
                        className="class-students-refund-consequence"
                        style={{ color: isFullRefund ? "var(--dl-color-warning-red)" : "var(--dl-color-gray-700)" }}>
                        {previewCents <= 0
                            ? "Enter an amount to refund."
                            : isFullRefund
                              ? "Full refund. This will remove the student from the class."
                              : "Partial refund. The student stays enrolled."}
                    </p>
                </div>
            </BaseModal>
        );
    }

    return (
        <>
            <BaseModal
                isOpen={!!student}
                onClose={onClose}
                title="Student Profile"
                icon={mdiAccountDetails}
                maxWidth={600}
                footer={
                    <>
                        <button
                            className={`class-students-inactive-toggle-btn${student.isInactive ? " class-students-inactive-toggle-btn--reactivate" : ""}`}
                            onClick={handleToggleInactive}
                            title={student.isInactive ? "Reactivate student" : "Place student on a break"}>
                            <Icon path={student.isInactive ? mdiAccountCheck : mdiAccountOff} size={0.8} />
                            {student.isInactive ? "Reactivate" : "Place on Break"}
                        </button>
                        {student.source === "student-profile" && student.email && (
                            <button
                                className="base-modal-save-btn"
                                onClick={openInviteEmailReview}
                                disabled={isSendingInvite}
                                title="Send account invite email"
                                style={{
                                    background: "var(--dl-color-turquoise-default)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}>
                                <Icon path={mdiEmailOutline} size={0.8} />
                                {isSendingInvite ? "Sending..." : "Send Invite"}
                            </button>
                        )}
                        <button className="base-modal-cancel-btn" onClick={onClose}>
                            Close
                        </button>
                        <button className="base-modal-save-btn" onClick={() => onEnrol(student)}>
                            Enrol in New Class
                        </button>
                    </>
                }>
                <div className="class-students-detail-header">
                    <div
                        className="class-students-avatar"
                        style={{ width: 48, height: 48, fontSize: "1rem", overflow: "hidden" }}>
                        {modalPhotoUrl ? (
                            <img src={modalPhotoUrl} alt="" className="class-students-avatar--img" />
                        ) : (
                            <Icon path={mdiAccountCircleOutline} size={1.5} color="var(--dl-color-gray-500)" />
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div className="class-students-name-row">
                            <span className="class-students-name">
                                {student.firstName} {student.lastName}
                            </span>
                            {student.source === "student-profile" ? (
                                <div className="class-students-account-banner class-students-account-banner--school-only">
                                    <Icon path={mdiAccountCircleOutline} size={0.7} />
                                    <span>School account only</span>
                                </div>
                            ) : (
                                <div className="class-students-account-banner class-students-account-banner--platform">
                                    <Icon path={mdiPaw} size={0.7} color="var(--dl-color-purple-default)" />
                                    <span>Dance Localé member</span>
                                </div>
                            )}
                            {student.source === "student-profile" && (
                                <button
                                    className="class-students-edit-profile-btn"
                                    title="Edit student profile"
                                    onClick={() => setIsEditingProfile(true)}>
                                    <Icon path={mdiAccountEditOutline} size={1.2} color="var(--dl-color-purple-1000)" />
                                </button>
                            )}
                            {(student.source === "student-profile" || student.source === "claimed-account") && (
                                <button
                                    className="class-students-remove-btn"
                                    title="Remove this student from your school"
                                    onClick={onRequestRemove}
                                    style={student.source === "claimed-account" ? { marginTop: 0, marginLeft: "auto" } : { marginTop: 0 }}>
                                    <Icon path={mdiTrashCanOutline} size={0.85} color="var(--dl-color-danger)" />
                                </button>
                            )}
                        </div>
                        <div className="class-students-meta">{student.email || "No email"}</div>
                    </div>
                </div>
                {isEditingProfile && (
                    <EditStudentProfileModal
                        student={student}
                        orgId={orgId}
                        onClose={() => setIsEditingProfile(false)}
                        onSaved={updatedProfile => {
                            student.firstName = updatedProfile.firstName;
                            student.lastName = updatedProfile.lastName;
                            student.email = updatedProfile.email;
                            student.phone = updatedProfile.phone;
                            setIsEditingProfile(false);
                            loadHistory();
                        }}
                    />
                )}

                {student.isInactive && (
                    <div className="class-students-inactive-banner">
                        <Icon path={mdiAccountOff} size={0.85} />
                        <span>
                            This student is currently <strong>on a break</strong>. Their enrolments are paused, so
                            they won't appear in check-in rosters or receive class emails. Click{" "}
                            <strong>Reactivate</strong> below to bring them back.
                        </span>
                    </div>
                )}

                {isLoading && (
                    <p className="class-students-loading" style={{ margin: "16px 0" }}>
                        Loading history...
                    </p>
                )}

                {!isLoading && (
                    <>
                        {currentEnrolments.length > 0 &&
                            (() => {
                                const termMap = new Map();
                                for (const e of currentEnrolments) {
                                    const tn = e.termName || "Current Term";
                                    if (!termMap.has(tn)) termMap.set(tn, e.termStartDate || e.startDate || "");
                                }
                                const termGroups = [...termMap.entries()]
                                    .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""))
                                    .map(([tn]) => tn);
                                return termGroups.map(tn => (
                                    <React.Fragment key={tn}>
                                        <div className="class-students-detail-section">{tn.toUpperCase()}</div>
                                        {currentEnrolments
                                            .filter(e => (e.termName || "Current Term") === tn)
                                            .map(e => {
                                                // Rolled-over enrolment in a future term that is not yet open
                                                // for enrolment: nothing is actionable, so no confirm button
                                                // and no "check in to confirm" prompt.
                                                const awaitingTermOpen =
                                                    e.status === "unconfirmed" &&
                                                    e.isFutureTerm &&
                                                    !e.termEnrolmentOpen;
                                                return (
                                                <div
                                                    key={e.id}
                                                    className="class-students-detail-enrolment class-students-detail-enrolment--with-actions">
                                                    <div className="class-students-detail-enrolment-info">
                                                        <span
                                                            className="class-students-name"
                                                            style={{ fontSize: "0.9rem" }}>
                                                            {e.className}
                                                        </span>
                                                        {e.dayOfWeek != null && (
                                                            <span className="class-students-meta">
                                                                {" "}
                                                                · {dayNames[e.dayOfWeek]} {e.startTime}
                                                            </span>
                                                        )}
                                                        <span
                                                            className={`class-students-badge ${
                                                                e.status === "confirmed" ? "enrolled" : "unconfirmed"
                                                            }`}
                                                            style={{ marginLeft: 8 }}>
                                                            {e.status === "confirmed"
                                                                ? "Enrolled"
                                                                : awaitingTermOpen
                                                                  ? "Rolled over \u2014 term not open yet"
                                                                  : "Unconfirmed \u2014 check in to confirm"}
                                                        </span>
                                                        {e.paymentPlan?.name && (
                                                            <div className="class-students-detail-plan">
                                                                {e.paymentPlan.name} — {formatCurrencyFromMinor(e.paymentPlan.price)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {e.status !== "cancelled" && (
                                                        <div className="class-students-detail-enrolment-actions">
                                                            {e.status !== "confirmed" && !awaitingTermOpen && (
                                                                <button
                                                                    className="class-students-action-btn class-students-action-btn--confirm"
                                                                    title="Mark as confirmed"
                                                                    disabled={statusChangingId === e.id}
                                                                    onClick={() =>
                                                                        handleStatusChange(e.id, "confirmed")
                                                                    }>
                                                                    <span className="class-students-action-btn-inner">
                                                                        <Icon path={mdiCheck} size={0.75} />
                                                                        Confirm
                                                                    </span>
                                                                </button>
                                                            )}
                                                            {offersRefunds &&
                                                                !awaitingTermOpen &&
                                                                (e.paymentStatus === "paid" ||
                                                                    e.paymentStatus === "partial-refund") && (
                                                                    <button
                                                                        className="class-students-action-btn class-students-action-btn--refund"
                                                                        title="Issue a refund (partial keeps them enrolled, full removes them)"
                                                                        onClick={() => handleRefundClick(e)}>
                                                                        <span className="class-students-action-btn-inner">
                                                                            <Icon path={mdiCashRefund} size={0.75} />
                                                                            Refund
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            <button
                                                                className="class-students-action-btn class-students-action-btn--remove"
                                                                title="Remove from class (no refund)"
                                                                onClick={() => handleRemoveClick(e)}>
                                                                <span className="class-students-action-btn-inner">
                                                                    <Icon path={mdiTrashCanOutline} size={0.75} />
                                                                    Remove
                                                                </span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                    </React.Fragment>
                                ));
                            })()}

                        {recentPastEnrolments.length > 0 && (
                            <div className="class-students-detail-section" style={{ marginTop: 16 }}>
                                {(recentPastEnrolments[0].termName || "Previous Term").toUpperCase()}
                            </div>
                        )}
                        {recentPastEnrolments.map(e => (
                            <div key={e.id} className="class-students-detail-enrolment">
                                <div>
                                    <span style={{ fontSize: "0.82rem", color: "#888" }}>{e.termName}</span>
                                    <span
                                        className="class-students-name"
                                        style={{ fontSize: "0.88rem", marginLeft: 6 }}>
                                        {e.className}
                                    </span>
                                    {e.dayOfWeek != null && (
                                        <span className="class-students-meta">
                                            {" "}
                                            · {dayNames[e.dayOfWeek]} {e.startTime}
                                        </span>
                                    )}
                                </div>
                                {e.paymentPlan?.name && (
                                    <div className="class-students-detail-plan">
                                        {e.paymentPlan.name} — {formatCurrencyFromMinor(e.paymentPlan.price)}
                                    </div>
                                )}
                            </div>
                        ))}

                        {history?.shortCourseEnrolments?.length > 0 && (
                            <>
                                <div className="class-students-detail-section" style={{ marginTop: 16 }}>
                                    SHORT COURSE ENROLMENTS
                                </div>
                                {history.shortCourseEnrolments.map(sc => (
                                    <div
                                        key={sc.id}
                                        className="class-students-detail-enrolment class-students-detail-enrolment--with-actions">
                                        <div className="class-students-detail-enrolment-info">
                                            <span className="class-students-name" style={{ fontSize: "0.88rem" }}>
                                                {sc.shortCourseName}
                                            </span>
                                            {[
                                                sc.style ? getDanceStyleLabel(sc.style) : null,
                                                sc.level,
                                                sc.sessionCount ? `${sc.sessionCount} sessions` : null,
                                                sc.startDate
                                                    ? new Date(sc.startDate).toLocaleDateString("en-AU", {
                                                          day: "numeric",
                                                          month: "short",
                                                          year: "numeric",
                                                      })
                                                    : null,
                                            ].filter(Boolean).length > 0 && (
                                                <span className="class-students-meta" style={{ marginLeft: 6 }}>
                                                    {[
                                                        sc.style ? getDanceStyleLabel(sc.style) : null,
                                                        sc.level,
                                                        sc.sessionCount ? `${sc.sessionCount} sessions` : null,
                                                        sc.startDate
                                                            ? new Date(sc.startDate).toLocaleDateString("en-AU", {
                                                                  day: "numeric",
                                                                  month: "short",
                                                                  year: "numeric",
                                                              })
                                                            : null,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </span>
                                            )}
                                            <span
                                                className={`class-students-badge ${
                                                    sc.status === "confirmed"
                                                        ? "enrolled"
                                                        : sc.status === "awaiting_approval_enr"
                                                          ? "awaiting_approval_enr"
                                                          : sc.status === "unconfirmed"
                                                            ? "unconfirmed"
                                                            : sc.status
                                                }`}
                                                style={{ marginLeft: 8 }}>
                                                {sc.status === "confirmed"
                                                    ? "Enrolled"
                                                    : sc.status === "awaiting_approval_enr"
                                                      ? "On hold"
                                                      : sc.status === "unconfirmed"
                                                        ? "Unconfirmed"
                                                        : sc.status}
                                            </span>
                                        </div>
                                        <button
                                            className="class-students-remove-btn"
                                            title="Remove from short course"
                                            onClick={() =>
                                                handleRemoveClick({ id: sc.id, className: sc.shortCourseName })
                                            }>
                                            <Icon path={mdiTrashCanOutline} size={0.85} />
                                        </button>
                                    </div>
                                ))}
                            </>
                        )}

                        {currentEnrolments.length === 0 &&
                            pastEnrolments.length === 0 &&
                            !history?.shortCourseEnrolments?.length && (
                                <p className="class-students-empty">No enrolment history</p>
                            )}

                        {/* Attendance Summary */}
                        {!attendanceLoading &&
                            attendance?.records?.length > 0 &&
                            (() => {
                                // Group records by classId
                                const classMap = {};
                                for (const rec of attendance.records) {
                                    if (!classMap[rec.classId]) {
                                        classMap[rec.classId] = {
                                            classId: rec.classId,
                                            className: rec.className,
                                            records: [],
                                        };
                                    }
                                    classMap[rec.classId].records.push(rec);
                                }
                                const classGroups = Object.values(classMap);
                                return (
                                    <>
                                        <div className="class-students-detail-section" style={{ marginTop: 16 }}>
                                            ATTENDANCE SUMMARY
                                        </div>
                                        {classGroups.map(g => {
                                            const totalSessions = attendance.classSessions?.[g.classId] || 0;
                                            const attended = g.records.filter(r => r.checkedIn).length;
                                            const pct =
                                                totalSessions > 0 ? Math.round((attended / totalSessions) * 100) : 0;
                                            const isExpanded = expandedClasses.has(g.classId);
                                            const sortedRecords = [...g.records].sort(
                                                (a, b) => new Date(b.date) - new Date(a.date)
                                            );
                                            return (
                                                <div key={g.classId} className="class-students-detail-attendance-item">
                                                    <div
                                                        className="class-students-detail-attendance-row"
                                                        onClick={() =>
                                                            setExpandedClasses(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(g.classId)) next.delete(g.classId);
                                                                else next.add(g.classId);
                                                                return next;
                                                            })
                                                        }>
                                                        <div className="class-students-detail-attendance-info">
                                                            <span className="class-students-detail-attendance-class">
                                                                {g.className}
                                                            </span>
                                                            <span className="class-students-detail-attendance-stat">
                                                                Attended {attended}/{totalSessions} ({pct}%)
                                                            </span>
                                                            <div className="class-students-detail-attendance-bar-bg">
                                                                <div
                                                                    className="class-students-detail-attendance-bar-fill"
                                                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <Icon
                                                            path={isExpanded ? mdiChevronUp : mdiChevronDown}
                                                            size={0.85}
                                                            color="var(--dl-color-gray-600)"
                                                        />
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="class-students-detail-attendance-sessions">
                                                            {sortedRecords.map((rec, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="class-students-detail-session-item">
                                                                    <Icon
                                                                        path={
                                                                            rec.isCancelled
                                                                                ? mdiCalendarRemove
                                                                                : rec.checkedIn
                                                                                  ? mdiCheckCircle
                                                                                  : mdiCloseCircle
                                                                        }
                                                                        size={0.8}
                                                                        color={
                                                                            rec.isCancelled
                                                                                ? "var(--dl-color-purple-900)"
                                                                                : rec.checkedIn
                                                                                  ? "var(--dl-color-greentick)"
                                                                                  : "var(--dl-color-gray-300)"
                                                                        }
                                                                    />
                                                                    <span className="class-students-detail-session-date">
                                                                        {rec.date
                                                                            ? new Date(rec.date).toLocaleDateString(
                                                                                  "en-AU",
                                                                                  {
                                                                                      weekday: "short",
                                                                                      day: "numeric",
                                                                                      month: "short",
                                                                                  }
                                                                              )
                                                                            : "—"}
                                                                    </span>
                                                                    {rec.isCancelled ? (
                                                                        <span className="class-students-badge cancelled">
                                                                            Session cancelled — Make-up owed
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            {rec.isMakeup && (
                                                                                <span className="class-students-badge makeup">
                                                                                    Make-up
                                                                                </span>
                                                                            )}
                                                                            {rec.isWalkIn && (
                                                                                <span className="class-students-badge walkin">
                                                                                    Walk-in
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                    </>
                )}
                {student.source === "student-profile" && student.email && (
                    <div className="class-students-invite-cta" style={{ marginTop: 16 }}>
                        <p className="class-students-invite-cta-text">
                            This student doesn&apos;t have a Dance Localé account yet. Send an invite so they can
                            download the app and unlock the full experience with online bookings etc.
                        </p>
                    </div>
                )}
            </BaseModal>
            <EmailReviewModal
                isOpen={showInviteEmailReview}
                emailType="studentInvite"
                templateData={{ firstName: student.firstName || "" }}
                onConfirm={handleConfirmInviteSend}
                onCancel={() => setShowInviteEmailReview(false)}
                isConfirming={isConfirmingInvite}
            />
        </>
    );
}

// ---------------------------------------------------------------------------
// ManualEnrolModal
// ---------------------------------------------------------------------------
function ManualEnrolModal({ student, orgId, onClose, onEnrolComplete, onPayAfterEnrol }) {
    const [classes, setClasses] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [enrolledIds, setEnrolledIds] = useState([]);
    const [shortCourses, setShortCourses] = useState([]);
    const [selectedScIds, setSelectedScIds] = useState([]);
    const [enrolledScIds, setEnrolledScIds] = useState([]);
    const [termGroups, setTermGroups] = useState([]); // [{ termId, termName, isFuture }, ...]
    const [isLoading, setIsLoading] = useState(true);
    const [isConfirming, setIsConfirming] = useState(false);
    const [step] = useState("select"); // single-step modal — always 'select'
    const [classSearch, setClassSearch] = useState("");

    useEffect(() => {
        if (!student) return;
        (async () => {
            setIsLoading(true);
            try {
                const [classRes, histRes, scRes, termRes] = await Promise.all([
                    classApi.getClasses(),
                    classApi.getStudentHistory(student.id),
                    classApi.getShortCourses(),
                    classApi.getTerms(),
                ]);

                // Eligible terms: current with enrolmentOpen + nearest future with enrolmentOpen.
                // Future term shown first (most actionable), then current term.
                const allTerms = termRes.ok ? termRes.data.terms || [] : [];
                const currentOpenTerms = allTerms.filter(t => t.isCurrent && t.enrolmentOpen);
                const nextOpenTerm = allTerms
                    .filter(t => t.isFuture && t.enrolmentOpen)
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];

                const eligibleTerms = [
                    ...(nextOpenTerm ? [{ ...nextOpenTerm, isFutureTerm: true }] : []),
                    ...currentOpenTerms.map(t => ({ ...t, isFutureTerm: false })),
                ];
                const eligibleTermIds = new Set(eligibleTerms.map(t => t.id));

                const allClasses = classRes.ok ? classRes.data.classes || classRes.data.classdefinitions || [] : [];
                const filteredClasses = allClasses
                    .filter(c => !c.termId || eligibleTermIds.has(c.termId))
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setClasses(filteredClasses);

                // Build term groups for grouped display (ordered: future first, then current).
                // Classes with no termId go into an "Other Classes" bucket at the end.
                setTermGroups(eligibleTerms.map(t => ({ termId: t.id, termName: t.name, isFutureTerm: t.isFutureTerm })));

                const allShortCourses = scRes.ok ? scRes.data.shortCourses || [] : [];
                setShortCourses(allShortCourses.filter(sc => !sc.isPast).sort((a, b) => (a.name || "").localeCompare(b.name || "")));

                // "Already enrolled" = has a current (non-cancelled) enrolment in one of the eligible terms.
                // We cross-reference by classId intersection: isCurrent from history AND that classId
                // exists in our filtered eligible class list.
                const eligibleClassIds = new Set(filteredClasses.map(c => c.id));
                const alreadyEnrolled = histRes.ok
                    ? (histRes.data.enrolments || [])
                          .filter(e => e.isCurrent && eligibleClassIds.has(e.classId))
                          .map(e => e.classId)
                    : [];
                setEnrolledIds(alreadyEnrolled);
                setSelectedIds([...alreadyEnrolled]);

                const alreadyEnrolledSc = histRes.ok
                    ? (histRes.data.shortCourseEnrolments || [])
                          .filter(e => e.status === "confirmed" || e.status === "unconfirmed")
                          .map(e => e.shortCourseId)
                    : [];
                setEnrolledScIds(alreadyEnrolledSc);
            } catch {
                toast.error("Failed to load classes");
            } finally {
                setIsLoading(false);
            }
        })();
    }, [student]);

    const toggleClass = classId => {
        if (enrolledIds.includes(classId)) return; // already enrolled — can't uncheck
        setSelectedIds(prev => (prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]));
    };

    const toggleShortCourse = scId => {
        if (enrolledScIds.includes(scId)) return;
        setSelectedScIds(prev => (prev.includes(scId) ? prev.filter(id => id !== scId) : [...prev, scId]));
    };

    const handleConfirm = async () => {
        const newIds = selectedIds.filter(id => !enrolledIds.includes(id));
        const newScIds = selectedScIds.filter(id => !enrolledScIds.includes(id));
        if (newIds.length === 0 && newScIds.length === 0) {
            toast.info("No new classes selected");
            return;
        }
        setIsConfirming(true);
        try {
            let classSuccessCount = 0;
            if (newIds.length > 0) {
                const r = await classApi.adminEnrolStudent(student.id, newIds);
                if (r.ok) {
                    classSuccessCount = (r.data.enrolments || []).filter(e => e.success).length;
                } else {
                    toast.error(r.data?.message || "Failed to enrol in classes");
                }
            }
            let scSuccessCount = 0;
            for (const scId of newScIds) {
                const r = await classApi.createEnrolment({
                    shortCourseId: scId,
                    studentProfileId: student.id,
                    status: "confirmed",
                });
                if (r.ok) scSuccessCount++;
                else toast.error(r.data?.message || "Failed to enrol in short course");
            }
            const total = classSuccessCount + scSuccessCount;
            if (total > 0) {
                toast.success(`Enrolled in ${total} class${total !== 1 ? "es" : ""}`);
                // Auto-clear inactive flag when a student is successfully enrolled
                if (student.isInactive) {
                    await classApi.setStudentInactive(student.id, false);
                }
            }
            if (onEnrolComplete) onEnrolComplete();

            // Only offer payment collection if at least one enrolment succeeded
            if (total === 0) {
                onClose();
                return;
            }

            // Hand off to the standard payment modal. It calls calculatePayment, which already
            // excludes any already-paid enrolments and surfaces a package picker scoped to the
            // newly-added (unpaid) classes — so adding a second class to a paid student is just a
            // clean second package assignment (with override), identical to first-time assignment.
            if (onPayAfterEnrol) onPayAfterEnrol(student);
            else onClose();
        } catch {
            toast.error("Failed to enrol student");
        } finally {
            setIsConfirming(false);
        }
    };

    if (!student) return null;

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
        <BaseModal
            isOpen={!!student}
            onClose={onClose}
            title={`Enrol ${student.firstName} ${student.lastName}`}
            icon={mdiAccountGroup}
            maxWidth={560}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={handleConfirm} disabled={isConfirming}>
                        {isConfirming ? "Enrolling..." : "Confirm Enrolment"}
                    </button>
                </>
            }>
            {step === "select" && (
                <>
                    <div className="class-students-modal-search-sticky">
                        <p style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "#777" }}>
                            Select the class you wish to enrol this student in
                        </p>
                        <input
                            className="class-students-modal-search-input"
                            type="text"
                            placeholder="Filter classes..."
                            value={classSearch}
                            onChange={e => setClassSearch(e.target.value)}
                        />
                    </div>
                    {isLoading && <p className="class-students-loading">Loading classes...</p>}
                    {!isLoading && classes.length === 0 && termGroups.length === 0 && (
                        <p className="class-students-empty">No classes available for enrolment</p>
                    )}
                    {!isLoading && classes.length === 0 && termGroups.length > 0 && (
                        <p className="class-students-empty">No classes have been set up for the open terms yet</p>
                    )}
                    {!isLoading && (() => {
                        const renderClassRow = cls => {
                            const isAlreadyEnrolled = enrolledIds.includes(cls.id);
                            const isChecked = selectedIds.includes(cls.id);
                            return (
                                <div
                                    key={cls.id}
                                    className={`class-students-enrol-row${isAlreadyEnrolled ? " already-enrolled" : ""}`}>
                                    <CustomFormInput
                                        type="checkbox"
                                        variant="turquoise"
                                        checked={isChecked}
                                        disabled={isAlreadyEnrolled}
                                        onChange={() => toggleClass(cls.id)}
                                        label={
                                            <>
                                                {cls.name}
                                                {cls.dayOfWeek != null &&
                                                    ` - ${dayNames[cls.dayOfWeek]} ${cls.startTime || ""}`}
                                                {isAlreadyEnrolled && (
                                                    <span className="class-students-class-tag">
                                                        Already enrolled
                                                    </span>
                                                )}
                                            </>
                                        }
                                    />
                                </div>
                            );
                        };

                        const searchLower = classSearch.toLowerCase();
                        const matchesSearch = cls => !classSearch || cls.name?.toLowerCase().includes(searchLower);

                        if (termGroups.length > 0) {
                            // Grouped view: one section per eligible term
                            const unterminated = classes.filter(c => !c.termId && matchesSearch(c));
                            return (
                                <>
                                    {termGroups.map(group => {
                                        const groupClasses = classes.filter(
                                            c => c.termId === group.termId && matchesSearch(c)
                                        );
                                        if (groupClasses.length === 0) return null;
                                        return (
                                            <div key={group.termId}>
                                                <div className="class-students-modal-section-header">
                                                    {group.termName}
                                                    {group.isFutureTerm && (
                                                        <span className="class-students-class-tag class-students-class-tag--future">
                                                            Upcoming
                                                        </span>
                                                    )}
                                                </div>
                                                {groupClasses.map(renderClassRow)}
                                            </div>
                                        );
                                    })}
                                    {unterminated.length > 0 && (
                                        <div>
                                            <div className="class-students-modal-section-header">Other Classes</div>
                                            {unterminated.map(renderClassRow)}
                                        </div>
                                    )}
                                    {classSearch && classes.filter(matchesSearch).length === 0 && (
                                        <p className="class-students-empty">No classes match "{classSearch}"</p>
                                    )}
                                </>
                            );
                        }

                        // Flat view fallback (no term structure)
                        const flatFiltered = classes.filter(matchesSearch);
                        return (
                            <>
                                {flatFiltered.map(renderClassRow)}
                                {classSearch && flatFiltered.length === 0 && (
                                    <p className="class-students-empty">No classes match "{classSearch}"</p>
                                )}
                            </>
                        );
                    })()}
                    {!isLoading && shortCourses.length > 0 && (
                        <>
                            <div className="class-students-modal-section-header">Short Courses</div>
                            {shortCourses
                                .filter(
                                    sc => !classSearch || sc.name?.toLowerCase().includes(classSearch.toLowerCase())
                                )
                                .map(sc => {
                                    const isAlreadyEnrolled = enrolledScIds.includes(sc.id);
                                    const isChecked = selectedScIds.includes(sc.id);
                                    return (
                                        <div
                                            key={sc.id}
                                            className={`class-students-enrol-row${isAlreadyEnrolled ? " already-enrolled" : ""}`}>
                                            <CustomFormInput
                                                type="checkbox"
                                                variant="turquoise"
                                                checked={isChecked}
                                                disabled={isAlreadyEnrolled}
                                                onChange={() => toggleShortCourse(sc.id)}
                                                label={
                                                    <>
                                                        {sc.name}
                                                        {sc.startDate &&
                                                            ` - starts ${new Date(sc.startDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`}
                                                        {isAlreadyEnrolled && (
                                                            <span className="class-students-class-tag">
                                                                Already enrolled
                                                            </span>
                                                        )}
                                                    </>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                        </>
                    )}
                </>
            )}

        </BaseModal>
    );
}

// ---------------------------------------------------------------------------
// StudentAvatarCell — per-row photo widget for student-profile / claimed-account
// students. Handles its own photo loading, preview modal, and force-picker.
// Renders plain initials for account-only students (no upload widget).
// ---------------------------------------------------------------------------
function StudentAvatarCell({ student }) {
    const [photoUrl, setPhotoUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [forcePickerOpen, setForcePickerOpen] = useState(0);

    const canUpload = student.source === "student-profile" || student.source === "claimed-account";
    const studentProfileId = student.id;

    useEffect(() => {
        if (!student.avatarKey) return;
        const cacheKey =
            student.studentPhotoVersion != null
                ? `student_${studentProfileId}_v${student.studentPhotoVersion}`
                : `student_account_${student.avatarKey}`;

        // Synchronous check — memory cache hit means instant display, no loading state
        const cached = getCachedImage(cacheKey);
        if (cached) {
            setPhotoUrl(base64ToBlobUrl(cached));
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        getOrFetchImage(cacheKey, "profile", () =>
            classApi
                .getStudentPhotoBase64(studentProfileId)
                .then(res => (res.ok && res.data?.imageBase64 ? res.data.imageBase64 : null))
        )
            .then(base64 => {
                if (!cancelled && base64) setPhotoUrl(base64ToBlobUrl(base64));
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [studentProfileId, student.avatarKey, student.studentPhotoVersion]);

    // Account-only students: display avatar or initials — no upload widget
    if (!canUpload) {
        const initials = `${student.firstName?.[0] || ""}${student.lastName?.[0] || ""}`.toUpperCase() || "?";
        if (isLoading) return <div className="class-students-avatar-loading" />;
        if (photoUrl) {
            return (
                <div className="class-students-avatar class-students-avatar--cell">
                    <img src={photoUrl} alt="" className="class-students-avatar--img" />
                </div>
            );
        }
        return (
            <div className="class-students-avatar" aria-hidden="true">
                {initials}
            </div>
        );
    }

    const handleRemovePhoto = async () => {
        setShowPreviewModal(false);
        const res = await classApi.deleteStudentPhoto(studentProfileId);
        if (res.ok) {
            toast.success("Photo removed.");
            setPhotoUrl(null);
        } else {
            toast.error(res.data?.message || "Failed to remove photo.");
        }
    };

    return (
        <div className="class-students-avatar-cell" onClick={e => e.stopPropagation()}>
            {isLoading ? (
                <div className="class-students-avatar-loading" />
            ) : (
                <StudentAvatarUpload
                    currentImage={photoUrl}
                    studentProfileId={studentProfileId}
                    forcePickerOpen={forcePickerOpen}
                    onPreviewClick={() => setShowPreviewModal(true)}
                    onSaveSuccess={base64 => {
                        const cacheKey = `student_${studentProfileId}_v${Date.now()}`;
                        cacheImage(cacheKey, base64);
                        setPhotoUrl(base64ToBlobUrl(base64));
                    }}
                    onRemove={() => setPhotoUrl(null)}
                />
            )}
            {showPreviewModal && (
                <StudentPhotoPreviewModal
                    isOpen={showPreviewModal}
                    photoUrl={photoUrl}
                    onClose={() => setShowPreviewModal(false)}
                    onChangePhoto={() => {
                        setShowPreviewModal(false);
                        setForcePickerOpen(p => p + 1);
                    }}
                    onRemovePhoto={handleRemovePhoto}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// AllStudentsPanel
// ---------------------------------------------------------------------------
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AllStudentsPanel({ orgId }) {
    // Always load full list — filter client-side so counts are always available
    const [allStudents, setAllStudents] = useState([]);
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const [unconfirmedStudents, setUnconfirmedStudents] = useState([]);
    const [inactiveStudents, setInactiveStudents] = useState([]);
    const [notEnrolledStudents, setNotEnrolledStudents] = useState([]);
    const [filter, setFilter] = useState("enrolled");
    const [search, setSearch] = useState("");
    const [sortMode, setSortMode] = useState("lastName");
    const [isLoading, setIsLoading] = useState(false);
    const [terms, setTerms] = useState([]);
    const [selectedTermId, setSelectedTermId] = useState(null);
    const [classes, setClasses] = useState([]);
    const [shortCourses, setShortCourses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState("");

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [enrollingStudent, setEnrollingStudent] = useState(null);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [paymentModalStudent, setPaymentModalStudent] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, confirmText?, cancelText?, onConfirm }
    const [offersRefunds, setOffersRefunds] = useState(false); // school setting — gates all refund UI

    // Load the school's offersRefunds setting once — gates the Refund button + payment-modal refund.
    // getSettings returns the settings object directly (response.data.settings), so read .offersRefunds.
    useEffect(() => {
        if (!orgId) return;
        organizationApi.getSettings(orgId)
            .then(res => setOffersRefunds(res?.offersRefunds === true))
            .catch(() => { /* non-fatal — defaults to no refund controls */ });
    }, [orgId]);

    const loadAll = useCallback(async () => {
        if (selectedTermId === null) return; // terms not yet resolved
        setIsLoading(true);
        try {
            const tid = selectedTermId || undefined;
            const [studRes, enrolledRes, inactiveRes, notEnrolledRes, unconfirmedRes] = await Promise.all(
                [
                    classApi.getAllStudents("all", tid),
                    classApi.getAllStudents("enrolled", tid),
                    classApi.getAllStudents("inactive", tid),
                    classApi.getAllStudents("not-enrolled", tid),
                    classApi.getAllStudents("unconfirmed", tid),
                ]
            );
            if (studRes.ok) setAllStudents(studRes.data.students || []);
            else toast.error(studRes.data?.message || "Failed to load students");
            if (enrolledRes.ok) setEnrolledStudents(enrolledRes.data.students || []);
            if (inactiveRes.ok) setInactiveStudents(inactiveRes.data.students || []);
            if (notEnrolledRes.ok) setNotEnrolledStudents(notEnrolledRes.data.students || []);
            if (unconfirmedRes.ok) setUnconfirmedStudents(unconfirmedRes.data.students || []);
        } catch {
            toast.error("Failed to load students");
        } finally {
            setIsLoading(false);
        }
    }, [selectedTermId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);


    useEffect(() => {
        classApi.getTerms().then(r => {
            const termList = r.ok ? r.data.terms || [] : [];
            setTerms(termList);
            const current = termList.find(t => t.isCurrent);
            // null → resolved: set to current term id or "" (all terms)
            setSelectedTermId(current ? current.id : "");
        });
    }, []);

    // Load class definitions for the selected term
    useEffect(() => {
        if (!selectedTermId) {
            setClasses([]);
            return;
        }
        classApi.getClasses(selectedTermId).then(r => {
            setClasses(r.ok ? r.data.classes || r.data || [] : []);
        });
    }, [selectedTermId]);

    // Load short courses once — term-independent, filter dropdown shows active (non-past) ones
    useEffect(() => {
        classApi.getShortCourses().then(r => {
            const courses = r.ok ? r.data.shortCourses || [] : [];
            setShortCourses(courses.filter(sc => !sc.isPast));
        });
    }, []);

    // Reset class filter whenever the term changes
    useEffect(() => {
        setSelectedClassId("");
    }, [selectedTermId]);

    // Not Enrolled card "..." menu shortcut. Routes through the same warning +
    // API path as the profile footer button.
    const handleDismissReEnrolment = student => handleRequestPlaceOnBreak(student);

    // Map enrolledStudents by id so all filter views can access real payment data
    const enrolledDataMap = useMemo(() => {
        const map = {};
        for (const s of enrolledStudents) {
            map[s.id] = s;
        }
        return map;
    }, [enrolledStudents]);

    // Counts for filter buttons
    const counts = {
        all: allStudents.length,
        enrolled: enrolledStudents.length,
        unconfirmed: unconfirmedStudents.length,
        "not-enrolled": notEnrolledStudents.length,
        inactive: inactiveStudents.length,
    };

    // Determine which list to display
    const baseList =
        filter === "not-enrolled"
            ? notEnrolledStudents
            : filter === "enrolled"
              ? enrolledStudents
              : filter === "unconfirmed"
                ? unconfirmedStudents
                : filter === "inactive"
                  ? inactiveStudents
                  : allStudents;

    // Client-side filter: class/short-course + search
    const filtered = baseList.filter(s => {
        if (selectedClassId) {
            const enrolledInClass = (s.currentEnrolments || []).some(
                e => e.classId === selectedClassId || e.shortCourseId === selectedClassId
            );
            if (!enrolledInClass) return false;
        }
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            s.firstName?.toLowerCase().includes(q) ||
            s.lastName?.toLowerCase().includes(q) ||
            s.email?.toLowerCase().includes(q)
        );
    });

    // Client-side sort
    const sorted = [...filtered].sort((a, b) => {
        if (sortMode === "lastName") {
            const cmp = (a.lastName || "").localeCompare(b.lastName || "");
            if (cmp !== 0) return cmp;
            return (a.firstName || "").localeCompare(b.firstName || "");
        }
        const cmp = (a.firstName || "").localeCompare(b.firstName || "");
        if (cmp !== 0) return cmp;
        return (a.lastName || "").localeCompare(b.lastName || "");
    });

    const getDisplayName = s =>
        sortMode === "lastName"
            ? `${s.lastName || ""}${s.lastName && s.firstName ? ", " : ""}${s.firstName || ""}`
            : `${s.firstName || ""} ${s.lastName || ""}`.trim();

    const handleOpenDetail = s => {
        setSelectedStudent(s);
    };
    const handleCloseDetail = () => {
        setSelectedStudent(null);
        loadAll();
    };
    // Single source of truth for the "place a student on a break" warning + action.
    // Called from two entry points: the profile-modal footer button, and the
    // Not Enrolled card "..." menu shortcut. Both surface the same modal copy
    // and hit the same API. UI label is "On Break"; data field is `isInactive`.
    const handleRequestPlaceOnBreak = student => {
        const name = `${student.firstName || ""} ${student.lastName || ""}`.trim();
        const profileId = student.id;
        setConfirmModal({
            title: name ? `Place ${name} on a break?` : "Place this student on a break?",
            message:
                "While on a break, this student will:\n" +
                "•  Be hidden from class check-in rosters\n" +
                "•  Be skipped on term rollover and enrolment confirmation emails\n" +
                "•  Be hidden from the public enrolment page\n\n" +
                "Their existing enrolments are preserved. To bring them back, go to the " +
                "On Break filter, open their profile, and click Reactivate.",
            variant: "warning",
            confirmText: "Place on Break",
            maxWidth: 384,
            onConfirm: async () => {
                try {
                    const r = await classApi.setStudentInactive(profileId, true);
                    if (r.ok) {
                        toast.success(name ? `${name} has been placed on a break` : "Student placed on a break");
                        student.isInactive = true;
                        setConfirmModal(null);
                        setSelectedStudent(null);
                        loadAll();
                    } else {
                        toast.error(r.data?.message || "Failed to update student");
                        setConfirmModal(null);
                    }
                } catch {
                    toast.error("Failed to update student");
                    setConfirmModal(null);
                }
            },
        });
    };
    const handleRequestRemove = student => {
        const name = `${student.firstName || ""} ${student.lastName || ""}`.trim();
        const profileId = student.id;
        // Delete = get rid of the school's record of them. Backend keeps a de-identified stub
        // ONLY if they've paid (legal retention), else deletes outright; their own account is
        // never touched. The account-holder note just reassures on that last point.
        const isAccountHolder = !!student.linkedAccountId || student.source === "claimed-account";
        setConfirmModal({
            title: name ? `Remove ${name} from your students?` : "Remove this student from your students?",
            message:
                "This deletes your school's record of this student. If they have paid you, those payment records " +
                "are kept for tax and accounting with their identity removed; everything else is deleted. Otherwise " +
                "the record is deleted entirely. This cannot be undone." +
                (isAccountHolder
                    ? "\n\nTheir own Dance Localé account is not affected. Only your school's record of them is removed."
                    : ""),
            variant: "danger",
            confirmText: "Remove",
            maxWidth: 420,
            onConfirm: async () => {
                try {
                    const r = await classApi.removeStudentFromRoster(profileId);
                    if (r.ok) {
                        toast.success(name ? `${name} removed from your students` : "Student removed");
                        setConfirmModal(null);
                        setSelectedStudent(null);
                        loadAll();
                    } else {
                        toast.error(r.data?.message || "Failed to remove student");
                        setConfirmModal(null);
                    }
                } catch {
                    toast.error("Failed to remove student");
                    setConfirmModal(null);
                }
            },
        });
    };
    const handleReactivate = async student => {
        try {
            const r = await classApi.setStudentInactive(student.id, false);
            if (r.ok) {
                const name = `${student.firstName || ""} ${student.lastName || ""}`.trim();
                toast.success(name ? `${name} reactivated and back from break` : "Student reactivated");
                student.isInactive = false;
                setSelectedStudent(null);
                loadAll();
            } else {
                toast.error(r.data?.message || "Failed to update student");
            }
        } catch {
            toast.error("Failed to update student");
        }
    };
    const handleOpenEnrol = s => {
        setSelectedStudent(null);
        setEnrollingStudent(s);
    };
    const handleCloseEnrol = () => setEnrollingStudent(null);
    const handleEnrolComplete = () => loadAll();

    const handleEnrolAndCollect = student => {
        setEnrollingStudent(null);
        setConfirmModal({
            title: "Student enrolled",
            message: `${student.firstName} ${student.lastName} has been enrolled. Would you like to assign a payment package now?`,
            variant: "primary",
            confirmText: "Assign Package",
            cancelText: "Not Now",
            onConfirm: () => {
                setConfirmModal(null);
                setPaymentModalStudent(student);
            },
        });
    };

    const handlePayment = (e, s) => {
        e.stopPropagation(); // prevent card click from also opening StudentDetailModal
        setPaymentModalStudent(s);
    };

    const handleExportCsv = () => {
        if (!sorted.length) return;

        const headers = ["First Name", "Last Name", "Email", "Phone", "Status", "Account Type"];
        const rows = sorted.map(s => [
            s.firstName || "",
            s.lastName || "",
            s.email || "",
            s.phone || "",
            s.currentTermStatus || (s.previousClass ? "Not Enrolled" : "Walk-in"),
            s.source || "",
        ]);

        const csv = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            .join("\n");

        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `students-${filter}-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const PRIMARY_FILTERS = [
        { value: "all", label: "All" },
        { value: "enrolled", label: "Enrolled" },
        { value: "unconfirmed", label: "Unconfirmed" },
        { value: "not-enrolled", label: "Not Enrolled" },
        { value: "inactive", label: "On Break" },
    ];

    // A next-term rollover line. No payment pill: a future-term enrolment has no price
    // package yet, so payment state is not meaningful (and must not read as a problem).
    // When the term is not yet open for enrolment there is nothing for the admin to chase,
    // so the pill says so rather than a bare "Unconfirmed".
    const renderFutureEnrolmentLine = enrol => {
        const dayLabel = enrol.dayOfWeek != null ? DAYS_SHORT[enrol.dayOfWeek] : null;
        const schedule = [dayLabel, enrol.startTime].filter(Boolean).join(" ");
        const notOpenYet =
            enrol.enrolmentStatus === "unconfirmed" && enrol.termEnrolmentOpen === false;
        return (
            <div className="class-students-enrolled-line" key={enrol.enrolmentId}>
                <div className="class-students-enrolled-class-col">
                    <span className="class-students-enrolled-class-name">{enrol.className}</span>
                    {schedule && (
                        <span className="class-students-enrolled-class-schedule"> · {schedule}</span>
                    )}
                    {enrol.termName && (
                        <span className="class-students-term-text">{enrol.termName}</span>
                    )}
                </div>
                <div className="class-students-enrolled-status-col">
                    <span
                        className={`class-students-badge class-students-badge--inline ${
                            enrol.enrolmentStatus === "confirmed" ? "enrolled" : "unconfirmed"
                        }`}>
                        {enrol.enrolmentStatus === "confirmed"
                            ? "Enrolled"
                            : notOpenYet
                              ? "Rolled over, term not open yet"
                              : "Unconfirmed"}
                    </span>
                </div>
            </div>
        );
    };

    const renderCard = s => {
        const displayName = getDisplayName(s);

        // Enrich with payment data from enrolledStudents when available.
        // Only prefer the enrolled entry when it actually carries enrolments — a student
        // present in the enrolled list with an empty array would otherwise overwrite the
        // enrolment data this filter's own response already returned.
        const enrolledEntry = enrolledDataMap[s.id];
        const enriched = enrolledEntry?.currentEnrolments?.length ? enrolledEntry : s;
        const currentEnrolments = enriched.currentEnrolments || [];
        // Next-term rollover reservations. Read from the row's own response, never the
        // enrolled-filter entry — the enrichment above exists only for current-term
        // payment data and would drop these.
        const futureEnrolments = s.futureEnrolments || [];

        // Helper: is this enrolment truly fully paid, instalment-plan aware?
        // partial-refund counts as paid — it means money was paid and only part refunded;
        // full refunds get status 'refunded' and correctly fail this check.
        const isEnrolmentFullyPaid = e => {
            if (e.paymentStatus !== "paid" && e.paymentStatus !== "partial-refund") return false;
            if (e.instalmentsTotal > 1 && e.instalmentsPaid < e.instalmentsTotal) return false;
            return true;
        };

        // Only confirmed enrolments whose term/course hasn't ended drive the payment icon.
        // Unconfirmed (rollover) enrolments have no payment record yet and would otherwise
        // make a fully paid student look partial. They're still shown on the card pills.
        const activeEnrolments = currentEnrolments.filter(
            e => e.isTermActive !== false && e.enrolmentStatus === 'confirmed'
        );

        // isPaid: true only when actual payment data confirms all active enrolments paid (instalment-aware)
        const isPaid = activeEnrolments.length > 0 && activeEnrolments.every(isEnrolmentFullyPaid);
        // Full-history outstanding flag: any awaiting_payment at this org, regardless of term
        const hasHistoricalOutstanding = s.hasOutstandingPayments === true;
        // External paid: all active enrolments paid but at least one was marked paid outside the platform
        const isExternalPaid = isPaid && activeEnrolments.some(e => e.paidOutsidePlatform === true);
        // Green solid check only when fully confirmed: all paid, no outstanding history, no external payments
        const effectiveIsPaid = isPaid && !hasHistoricalOutstanding && !isExternalPaid;
        // Purple clock: only for instalment plans actively in progress (some instalments paid, more expected)
        const isInstalmentInProgress = !isPaid && activeEnrolments.some(
            e => e.instalmentsTotal > 1 && e.instalmentsPaid > 0 && e.instalmentsPaid < e.instalmentsTotal
        );
        // isPending: fallback to currentTermStatus when no enrolment detail available (simple list view)
        const isPending =
            activeEnrolments.length > 0
                ? activeEnrolments.some(e => e.paymentStatus !== "paid")
                : s.currentTermStatus === "awaiting_approval_enr";
        // The orange "Payment outstanding" icon is CURRENT-TERM scoped only — it must
        // open a payment modal that can actually act on the debt. A past-term balance
        // (hasHistoricalOutstanding) is deliberately NOT surfaced here: clicking it
        // would dead-end ("current term up to date") because the modal is term-scoped.
        // Cross-term outstanding belongs in the planned Payments audit tab (see PRP
        // research note: student-payment-audit-tab). hasHistoricalOutstanding is still
        // used to suppress the green "fully paid" tick (effectiveIsPaid) so we don't
        // show a paid checkmark on someone who owes from a prior term.
        const hasCurrentTermOutstanding = activeEnrolments.some(e =>
            e.paymentStatus != null &&
            e.paymentStatus !== "paid" &&
            e.paymentStatus !== "partial-refund"
        );
        // Pricing not yet assigned on a confirmed enrolment — admin must assign a price
        // package before payment can be collected. Distinct from "awaiting payment".
        const isPricingPending =
            !isPaid &&
            !isInstalmentInProgress &&
            activeEnrolments.some(e => e.paymentStatus === null);

        // List view — expanded layout when enrolment data is available
        if (currentEnrolments.length > 0 || futureEnrolments.length > 0) {
            return (
                <div
                    key={s.id}
                    className="class-students-row class-students-row--enrolled class-students-row--clickable"
                    onClick={() => handleOpenDetail(s)}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOpenDetail(s);
                        }
                    }}
                    role="button"
                    tabIndex={0}>
                    <StudentAvatarCell student={s} />
                    {/* Left column: name + class rows with inline status pill */}
                    <div className="class-students-info">
                        <div className="class-students-name-row">
                            <span className="class-students-name">{displayName}</span>
                            <span
                                className="class-students-account-type-icon"
                                title={getAccountTypeIcon(s.source).label}>
                                <Icon
                                    path={getAccountTypeIcon(s.source).icon}
                                    size={0.85}
                                    color={
                                        s.source === "student-profile"
                                            ? "var(--dl-color-purple-900)"
                                            : "var(--dl-color-purple-default)"
                                    }
                                />
                            </span>
                        </div>
                        {currentEnrolments.map(enrol => {
                            const dayLabel = enrol.dayOfWeek != null ? DAYS_SHORT[enrol.dayOfWeek] : null;
                            let schedule;
                            if (enrol.shortCourseId && enrol.startDate) {
                                const d = new Date(enrol.startDate);
                                const dateStr = d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
                                schedule = [dayLabel, `from ${dateStr}`, enrol.startTime].filter(Boolean).join(" · ");
                            } else {
                                schedule = [dayLabel, enrol.startTime].filter(Boolean).join(" ");
                            }
                            return (
                                <div className="class-students-enrolled-line" key={enrol.enrolmentId}>
                                    {/* Left column: class name · schedule · term — single line */}
                                    <div className="class-students-enrolled-class-col">
                                        <span className="class-students-enrolled-class-name">{enrol.className}</span>
                                        {schedule && (
                                            <span className="class-students-enrolled-class-schedule"> · {schedule}</span>
                                        )}
                                        {enrol.termName && (
                                            <span className="class-students-term-text">{enrol.termName}</span>
                                        )}
                                    </div>
                                    {/* Right column: status pills — own column for quick glance */}
                                    <div className="class-students-enrolled-status-col">
                                        <span
                                            className={`class-students-badge class-students-badge--inline ${
                                                enrol.enrolmentStatus === "confirmed"
                                                    ? "enrolled"
                                                    : enrol.enrolmentStatus === "awaiting_approval_enr"
                                                      ? "awaiting_approval_enr"
                                                      : "unconfirmed"
                                            }`}>
                                            {enrol.enrolmentStatus === "confirmed"
                                                ? "Enrolled"
                                                : enrol.enrolmentStatus === "awaiting_approval_enr"
                                                  ? "On hold"
                                                  : "Unconfirmed"}
                                        </span>
                                        {enrol.isTermActive !== false &&
                                            enrol.enrolmentStatus === "confirmed" &&
                                            (() => {
                                                    // partial-refund means money was paid and only part refunded —
                                                    // full refunds get status 'refunded' (falls through to unpaid branch)
                                                    const paid =
                                                        enrol.paymentStatus === "paid" ||
                                                        enrol.paymentStatus === "partial-refund";
                                                    const fullyPaid =
                                                        enrol.instalmentsTotal > 1
                                                            ? paid && enrol.instalmentsPaid >= enrol.instalmentsTotal
                                                            : paid;
                                                    const partialInstalment =
                                                        enrol.instalmentsTotal > 1 &&
                                                        enrol.instalmentsPaid > 0 &&
                                                        !fullyPaid;
                                                    const isPricingPending = enrol.paymentStatus === null;
                                                    if (enrol.paymentStatus === "refunded") {
                                                        // Fully refunded — distinct pill, never the "Awaiting payment" fallback.
                                                        return (
                                                            <span className="class-students-badge class-students-badge--inline class-students-badge--pay-refunded">
                                                                Refunded
                                                            </span>
                                                        );
                                                    } else if (fullyPaid) {
                                                        // External (cash/bank) payments still need bank-statement
                                                        // reconciliation — amber "check the money" state, distinct
                                                        // from the green "fully settled" paid-in-full state.
                                                        const isExternal = enrol.paidOutsidePlatform === true;
                                                        return (
                                                            <span
                                                                className={`class-students-badge class-students-badge--inline ${
                                                                    isExternal
                                                                        ? "class-students-badge--pay-unreconciled"
                                                                        : "class-students-badge--pay-paid"
                                                                }`}>
                                                                {isExternal ? "Paid - unreconciled" : "Paid in full"}
                                                            </span>
                                                        );
                                                    } else if (partialInstalment) {
                                                        return (
                                                            <span className="class-students-badge class-students-badge--inline class-students-badge--pay-partial">
                                                                {enrol.instalmentsPaid}/{enrol.instalmentsTotal} paid
                                                            </span>
                                                        );
                                                    } else if (isPricingPending) {
                                                        return (
                                                            <span className="class-students-badge class-students-badge--inline class-students-badge--pay-pricing-pending">
                                                                Awaiting price package
                                                            </span>
                                                        );
                                                    } else {
                                                        return (
                                                            <span className="class-students-badge pay-unpaid class-students-badge--inline">
                                                                Awaiting payment
                                                            </span>
                                                        );
                                                    }
                                                })()}
                                    </div>
                                </div>
                            );
                        })}
                        {futureEnrolments.length > 0 && (
                            <div className="class-students-future-group">
                                <div className="class-students-future-heading">
                                    <Icon
                                        path={mdiCalendarArrowRight}
                                        size={0.6}
                                        color="var(--dl-color-green-750)"
                                    />
                                    <span>Next term</span>
                                </div>
                                {futureEnrolments.map(enrol => renderFutureEnrolmentLine(enrol))}
                            </div>
                        )}
                    </div>
                    {/* Right column: single payment icon for all enrolments */}
                    {(() => {
                        return (
                            <div className="class-students-enrolled-right">
                                <div className="class-students-card-actions">
                                    {isExternalPaid ? (
                                        <button
                                            className="class-students-payment-btn class-students-payment-btn--lg class-students-payment-btn--paid"
                                            onClick={e => handlePayment(e, s)}
                                            title="Paid externally. Reconcile in Manage Finances whenever you balance your books (optional).">
                                            <Icon
                                                path={mdiCreditCardSyncOutline}
                                                size={1.3}
                                                color="var(--dl-color-greentick)"
                                            />
                                        </button>
                                    ) : effectiveIsPaid ? (
                                        <button
                                            className="class-students-payment-btn class-students-payment-btn--lg class-students-payment-btn--paid"
                                            onClick={e => handlePayment(e, s)}
                                            title="Paid">
                                            <Icon
                                                path={mdiCreditCardCheck}
                                                size={1.3}
                                                color="var(--dl-color-greentick)"
                                            />
                                        </button>
                                    ) : isInstalmentInProgress ? (
                                        <button
                                            className="class-students-payment-btn class-students-payment-btn--lg"
                                            onClick={e => handlePayment(e, s)}
                                            title="Payment plan in progress">
                                            <Icon
                                                path={mdiCreditCardClockOutline}
                                                size={1.3}
                                                color="var(--dl-color-purple-900)"
                                            />
                                        </button>
                                    ) : isPricingPending ? (
                                        <button
                                            className="class-students-payment-btn class-students-payment-btn--lg"
                                            onClick={e => handlePayment(e, s)}
                                            title="Assign price package">
                                            <Icon
                                                path={mdiCreditCardPlusOutline}
                                                size={1.3}
                                                color="var(--dl-color-blue-default)"
                                            />
                                        </button>
                                    ) : hasCurrentTermOutstanding ? (
                                        <button
                                            className="class-students-payment-btn class-students-payment-btn--lg"
                                            onClick={e => handlePayment(e, s)}
                                            title="Payment outstanding">
                                            <Icon
                                                path={mdiCreditCardClockOutline}
                                                size={1.3}
                                                color="var(--dl-color-needs-action)"
                                            />
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            );
        }

        // Simple list card — no enrolment data available
        return (
            <div
                key={s.id}
                className="class-students-row class-students-row--clickable"
                onClick={() => handleOpenDetail(s)}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenDetail(s);
                    }
                }}
                role="button"
                tabIndex={0}>
                <StudentAvatarCell student={s} />
                <div className="class-students-info">
                    <div className="class-students-name-row">
                        <span className="class-students-name">{displayName}</span>
                        <span className="class-students-account-type-icon" title={getAccountTypeIcon(s.source).label}>
                            <Icon
                                path={getAccountTypeIcon(s.source).icon}
                                size={0.75}
                                color={
                                    s.source === "student-profile"
                                        ? "var(--dl-color-purple-900)"
                                        : "var(--dl-color-purple-default)"
                                }
                            />
                        </span>
                    </div>
                    <div className="class-students-meta">
                        {s.email || "No email"}
                        {s.isInactive && (
                            <span className="class-students-badge inactive" style={{ marginLeft: 8 }}>
                                On Break
                            </span>
                        )}
                    </div>
                    {filter === "not-enrolled" && s.previousClass && (
                        <div className="class-students-meta class-students-meta--subtle">
                            Previously: {s.previousClass}
                            {s.previousTermName ? ` (${s.previousTermName})` : ""}
                        </div>
                    )}
                </div>
                {filter === "not-enrolled" && (
                    <>
                        <button
                            className="btn-purple btn-small class-students-enrol-btn"
                            onClick={e => {
                                e.stopPropagation();
                                handleOpenEnrol(s);
                            }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Icon path={mdiAccountPlusOutline} size={0.8} />
                                Enrol
                            </span>
                        </button>
                        <div className="class-students-on-break-action">
                            <button
                                className="class-students-on-break-btn"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleDismissReEnrolment(s);
                                }}>
                                <Icon path={mdiAccountOff} size={0.85} />
                                <span>Place on Break</span>
                            </button>
                            <span className="class-students-on-break-hint">
                                Hides student from all other filters until re-enrolled
                            </span>
                        </div>
                    </>
                )}
                {filter !== "not-enrolled" && (
                    <div className="class-students-card-actions">
                        {!s.isInactive && isInstalmentInProgress && (
                            <button
                                className="class-students-payment-btn"
                                onClick={e => handlePayment(e, s)}
                                title="Payment plan in progress">
                                <Icon path={mdiCreditCardClockOutline} size={1} color="var(--dl-color-purple-900)" />
                            </button>
                        )}
                        {!s.isInactive && isPending && !isInstalmentInProgress && (
                            <button
                                className="class-students-payment-btn"
                                onClick={e => handlePayment(e, s)}
                                title="Payment outstanding">
                                <Icon path={mdiCreditCardClockOutline} size={1} color="var(--dl-color-needs-action)" />
                            </button>
                        )}
                        {isExternalPaid && (
                            <button
                                className="class-students-payment-btn class-students-payment-btn--paid"
                                onClick={e => handlePayment(e, s)}
                                title="Paid externally. Reconcile in Manage Finances whenever you balance your books (optional).">
                                <Icon path={mdiCreditCardSyncOutline} size={1} color="var(--dl-color-greentick)" />
                            </button>
                        )}
                        {effectiveIsPaid && (
                            <button
                                className="class-students-payment-btn class-students-payment-btn--paid"
                                onClick={e => handlePayment(e, s)}
                                title="Paid">
                                <Icon path={mdiCreditCardCheck} size={1} color="var(--dl-color-greentick)" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="class-students-page">
            {/* Toolbar row 1: term selector + class selector + search + export */}
            <div className="class-students-toolbar">
                <select
                    className="class-students-attendance-select class-students-term-select"
                    value={selectedTermId ?? ""}
                    onChange={e => setSelectedTermId(e.target.value)}>
                    <option value="">All Terms</option>
                    {terms.filter(t => !t.isPast).map(t => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
                <select
                    className="class-students-attendance-select class-students-class-select"
                    value={selectedClassId}
                    onChange={e => setSelectedClassId(e.target.value)}
                    disabled={(!selectedTermId || !classes.length) && !shortCourses.length}>
                    <option value="">
                        {selectedTermId || shortCourses.length ? "All Classes & Courses" : "Select a term first"}
                    </option>
                    {selectedTermId && classes.length > 0 && (
                        <optgroup label="Classes">
                            {[...classes]
                                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                .map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                        </optgroup>
                    )}
                    {shortCourses.length > 0 && (
                        <optgroup label="Short courses">
                            {[...shortCourses]
                                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                .map(sc => (
                                    <option key={sc.id} value={sc.id}>
                                        {sc.name}
                                    </option>
                                ))}
                        </optgroup>
                    )}
                </select>
                <div className="class-students-search-wrap">
                    <input
                        className="class-students-search"
                        type="text"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={e => {
                            const v = e.target.value;
                            setSearch(v);
                            // Searching looks across the whole school — reset the filter to "all" the
                            // moment a query is typed so a filtered view can't hide a match.
                            if (v && filter !== "all") setFilter("all");
                        }}
                    />
                    {search && (
                        <button
                            className="class-students-search-clear"
                            onClick={() => setSearch("")}
                            aria-label="Clear search">
                            <Icon path={mdiClose} size={0.7} />
                        </button>
                    )}
                </div>
                <button className="class-students-add-btn" onClick={() => setShowRegisterModal(true)}>
                    <Icon path={mdiAccountPlusOutline} size={0.85} /> Add Student
                </button>
            </div>

            {/* Toolbar row 2: filter bar with counts + export */}
            <div className="class-students-filter-bar">
                {PRIMARY_FILTERS.map(f => (
                    <button
                        key={f.value}
                        className={`class-students-filter-btn${filter === f.value ? " active" : ""}`}
                        onClick={() => setFilter(f.value)}>
                        {f.label}
                        {counts[f.value] > 0 && <span className="class-students-filter-count">{counts[f.value]}</span>}
                    </button>
                ))}
                <button className="class-students-export-btn" onClick={handleExportCsv} disabled={!sorted.length}>
                    <Icon path={mdiDownload} size={0.85} /> Export
                </button>
            </div>

            {/* Unconfirmed hint banner */}
            {filter === "unconfirmed" && !isLoading && (
                <div className="class-students-prospects-hint">
                    These are students <strong>rolled over</strong> from previous term and yet to enrol/confirm their
                    spot. A notification email may have been sent and is awaiting their response. Check them in a class
                    to confirm their enrolment, or send reminder email.
                </div>
            )}

            {/* On Break hint banner */}
            {filter === "inactive" && !isLoading && (
                <div className="class-students-inactive-hint">
                    These students are on a break. They won't appear in any other filter and are excluded from bulk
                    enrolment emails. To bring them back, click a student card and use the Reactivate button, or enrol
                    them in a new class.
                </div>
            )}

            {/* Student list / grid */}
            {isLoading && <p className="class-students-loading">Loading students...</p>}
            {!isLoading && sorted.length === 0 && (
                <p className="class-students-empty">
                    {search
                        ? "No students match your search"
                        : filter === "inactive"
                          ? "No students currently on a break"
                          : "No students found"}
                </p>
            )}

            {!isLoading && sorted.length > 0 && (
                <div className="class-students-list">{sorted.map(renderCard)}</div>
            )}

            {selectedStudent && (
                <StudentDetailModal
                    student={selectedStudent}
                    orgId={orgId}
                    offersRefunds={offersRefunds}
                    onClose={handleCloseDetail}
                    onEnrol={handleOpenEnrol}
                    onRequestPlaceOnBreak={() => handleRequestPlaceOnBreak(selectedStudent)}
                    onReactivate={() => handleReactivate(selectedStudent)}
                    onRequestRemove={() => handleRequestRemove(selectedStudent)}
                />
            )}
            {showRegisterModal && (
                <RegisterStudentModal
                    onClose={() => setShowRegisterModal(false)}
                    onCreated={newStudent => {
                        setShowRegisterModal(false);
                        loadAll();
                        // Chain into the existing class picker for the freshly-created student
                        setEnrollingStudent(newStudent);
                    }}
                />
            )}
            {enrollingStudent && (
                <ManualEnrolModal
                    student={enrollingStudent}
                    orgId={orgId}
                    onClose={handleCloseEnrol}
                    onEnrolComplete={handleEnrolComplete}
                    onPayAfterEnrol={handleEnrolAndCollect}
                />
            )}
            {paymentModalStudent && (
                <AdminPaymentModal
                    isOpen={!!paymentModalStudent}
                    onClose={() => setPaymentModalStudent(null)}
                    onSuccess={() => {
                        setPaymentModalStudent(null);
                        loadAll();
                    }}
                    studentProfileId={paymentModalStudent.id}
                    orgId={orgId}
                    termId={selectedTermId || undefined}
                    studentName={`${paymentModalStudent.firstName || ""} ${paymentModalStudent.lastName || ""}`.trim()}
                    offersRefunds={offersRefunds}
                />
            )}

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    confirmText={confirmModal.confirmText}
                    cancelText={confirmModal.cancelText}
                    maxWidth={confirmModal.maxWidth}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// ClassStudentsPage (shell)
// ---------------------------------------------------------------------------
const ClassStudentsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const orgId = user?.orgId || "";
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);
    const [enquiryEnrolStudent, setEnquiryEnrolStudent] = useState(null);

    const tabs = [
        {
            label: "Prospects",
            content: <ProspectsTab onEnrolStudent={setEnquiryEnrolStudent} />,
        },
        {
            label: "Students",
            content: <AllStudentsPanel orgId={orgId} />,
        },
        {
            label: "Attendance",
            content: <AttendanceTab orgId={orgId} />,
        },
        {
            label: "History",
            content: <HistoryTab orgId={orgId} />,
        },
        {
            label: "Payments",
            content: <PaymentsAuditTab orgId={orgId} />,
        },
        {
            label: "Print Class List",
            content: <PrintListTab orgId={orgId} />,
        },
    ];

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper class-students-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate("/classes/manage")} className="back-nav-btn">
                                ← Manage Classes
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiAccountGroup}
                                    size={1}
                                    color="#451f78"
                                    style={{ marginRight: 8, verticalAlign: "middle" }}
                                />
                                Students
                            </h1>
                            {/* Bulk import entry point — hidden for now; access via Manage Classes > Student Upload card */}
                            {/*
                            <div className="class-students-header-right">
                                <div className="class-students-import-prompt">
                                    <span className="class-students-import-prompt-label">New studio?</span>
                                    <button
                                        className="btn-turquoise btn-small"
                                        onClick={() => navigate("/classes/students/import")}>
                                        <Icon path={mdiAccountMultiplePlusOutline} size={0.7} /> Bulk import
                                    </button>
                                </div>
                            </div>
                            */}
                        </div>
                        <div className="classes-subtitle-row">
                            <p className="sub-portal-header-subtitle">
                                Manage your enrolled students, review new enquiries, and track attendance and payments.
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Students Help"
                                title="How It Works">
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.62rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    <FolderTabs tabs={tabs} defaultTab={1} />
                </div>
            </div>
            <ClassStudentsHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
            {enquiryEnrolStudent && (
                <ManualEnrolModal
                    student={enquiryEnrolStudent}
                    orgId={orgId}
                    onClose={() => setEnquiryEnrolStudent(null)}
                    onEnrolComplete={() => setEnquiryEnrolStudent(null)}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// EditStudentProfileModal — edit school-only student profile fields
// ---------------------------------------------------------------------------
function EditStudentProfileModal({ student, orgId, onClose, onSaved }) {
    const [form, setForm] = useState({
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        email: student.email || "",
        phone: student.phone || "",
        experienceNotes: student.experienceNotes || "",
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!form.firstName.trim()) {
            toast.error("First name is required");
            return;
        }
        setIsSaving(true);
        try {
            const r = await classApi.updateStudentProfile(student.id, form);
            if (r.ok) {
                toast.success("Student profile updated");
                onSaved(r.data.profile);
                onClose();
            } else {
                toast.error(r.data?.message || "Failed to update profile");
            }
        } catch {
            toast.error("Failed to update profile");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <BaseModal
            isOpen
            onClose={onClose}
            title="Edit Student Profile"
            icon={mdiAccountBoxEditOutline}
            maxWidth={480}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="base-modal-save-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </>
            }>
            <div className="class-students-edit-form">
                <label className="class-students-edit-label">First Name *</label>
                <input
                    className="class-students-edit-input"
                    value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                />

                <label className="class-students-edit-label">Last Name</label>
                <input
                    className="class-students-edit-input"
                    value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                />

                <label className="class-students-edit-label">Email</label>
                <input
                    className="class-students-edit-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />

                <label className="class-students-edit-label">Phone</label>
                <input
                    className="class-students-edit-input"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                />

                <label className="class-students-edit-label">Experience Notes</label>
                <input
                    className="class-students-edit-input"
                    value={form.experienceNotes}
                    onChange={e => setForm(f => ({ ...f, experienceNotes: e.target.value }))}
                />

                {student.email && (
                    <div className="class-students-invite-cta">
                        <p className="class-students-invite-cta-text">
                            This student doesn&apos;t have a Dance Localé account yet. Send an invite so they can
                            download the app and unlock the full experience with online bookings etc.
                        </p>
                    </div>
                )}
            </div>
        </BaseModal>
    );
}

export default ClassStudentsPage;
